import * as i0 from '@angular/core';
import { InjectionToken, Injectable, PLATFORM_ID, Inject, Optional, isDevMode, Component, NgModule, EventEmitter, Input, Output } from '@angular/core';
import { isPlatformBrowser, isPlatformServer, DOCUMENT, CommonModule } from '@angular/common';
import { Cookie } from 'ng2-cookies';
import { Cookie as Cookie$1 } from 'ng2-cookies/ng2-cookies';
import * as i2 from 'ngx-webstorage';
import { encrypt, decrypt } from 'crypto-js/aes';
import utf8 from 'crypto-js/enc-utf8';
import * as i3 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import * as i3$1 from '@angular/router';
import { NavigationStart } from '@angular/router';
import { timer, BehaviorSubject, filter, Subject } from 'rxjs';
import * as i1 from '@citi-gcg-173875/citi-common-ui-cds';
import { CdsIconModule, CdsModalDialogModule, CdsButtonModule } from '@citi-gcg-173875/citi-common-ui-cds';

const WINDOW = new InjectionToken('WindowToken');
/* Define abstract class for obtaining reference to the global window object. */
// @dynamic
class WindowRef {
    get nativeWindow() {
        // Had to make this function despite being an abstract class because teams are using it
        // if (isDevMode()) throw new Error(WindowRefError);
        if (window)
            return window;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: WindowRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: WindowRef, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: WindowRef, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
/* Define class that implements the abstract class and returns the native window object. */
// @dynamic
class BrowserWindowRef extends WindowRef {
    constructor(platformId) {
        super();
        this.platformId = platformId;
    }
    get nativeWindow() {
        if (isPlatformBrowser(this.platformId)) {
            return window;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserWindowRef, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserWindowRef, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserWindowRef, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });
/* Create an factory function that returns the native window object. */
function windowFactory(browserWindowRef, platformId) {
    if (isPlatformBrowser(platformId)) {
        return browserWindowRef.nativeWindow;
    }
    return null;
}
const browserWindowProvider = {
    provide: WindowRef,
    useClass: BrowserWindowRef
};
/* Create an injectable provider that uses the windowFactory function for returning the native window object. */
const windowProvider = {
    provide: WINDOW,
    useFactory: windowFactory,
    deps: [WindowRef, PLATFORM_ID]
};
/* Create an array of providers. */
const WINDOW_PROVIDER = [browserWindowProvider, windowProvider];

class CookieService {
    constructor(_window) {
        this._window = _window;
        this.localCookies = new Map();
    }
    /**
     * Retrieves a value from cookie storage with a specified name.
     * @param name Name of a cookie to retrieve.
     * @returns Value of a specified name. Returns an empty string if not found.
     */
    getCookie(name) {
        if (this.localCookies.has(name)) {
            return this.localCookies.get(name);
        }
        else if (this._window) {
            return Cookie.get(name);
        }
        return '';
    }
    /**
     * Stores a cookie into the storage.
     * @param cookie Cookie object.
     */
    setCookie(cookie) {
        if (!cookie) {
            return;
        }
        if (typeof cookie === 'string') {
            this.parseCookieString(cookie);
        }
        else if (this.isCookieInstance(cookie)) {
            this.setCookieInLocal(cookie.name, cookie.value);
            this.setCookieInStorage(cookie);
        }
        else if (Array.isArray(cookie)) {
            cookie.filter(this.isCookieInstance).forEach(pair => {
                this.setCookieInLocal(pair.name, pair.value);
                this.setCookieInStorage(pair);
            });
        }
    }
    /**
     * Deletes a cookie with specified parameters.
     * @param name Name of the cookie.
     * @param path Path that the cookie is set to.
     * @param domain Domain that the cookie is set to.
     */
    deleteCookie(name, path, domain) {
        this.localCookies.delete(name);
        if (!this._window) {
            return;
        }
        if (path && domain) {
            Cookie.delete(name, path, domain);
        }
        else if (path) {
            Cookie.delete(name, path);
        }
        else if (domain) {
            Cookie.delete(name, null, domain);
        }
        else {
            Cookie.delete(name);
        }
    }
    /**
     * Converts the string into a cookie object.
     * @param strPairs String object delimited by ';' and '='
     */
    parseCookieString(strPairs) {
        strPairs.split(';')
            .map(pairs => pairs.split('='))
            .filter(pair => pair.length === 2)
            .filter(pair => pair[0].length > 0)
            .filter(pair => pair[1].length > 0)
            .forEach(pair => {
            const [name, val] = pair;
            this.setCookieInLocal(name, val);
            this.setCookieInStorage(name, val);
        });
    }
    /**
     * Stores the cookie into the Cookie storage object.
     * @param cookie Name or object of the cookie.
     * @param value Value that the specified cookie contains.
     */
    setCookieInStorage(cookie, value) {
        if (!this._window) {
            return;
        }
        if (typeof cookie === 'string') {
            Cookie.set(this.getTrimmedString(cookie), this.getTrimmedString(value));
        }
        else {
            Cookie.set(this.getTrimmedString(cookie.name), this.getTrimmedString(cookie.value), cookie.expires, cookie.path ? cookie.path : this._window.location.pathname, cookie.domain ? cookie.domain : this._window.location.hostname, false);
        }
    }
    /**
     * Stores the cookie locally.
     * @param name Name or object of the cookie.
     * @param value Value that the specified cookie contains.
     */
    setCookieInLocal(name, value) {
        if (name) {
            this.localCookies.set(this.getTrimmedString(name), this.getTrimmedString(value));
        }
    }
    /**
     * Returns a string that has leading and trailing whitespaces removed if exists.
     * @param str String value.
     */
    getTrimmedString(str) {
        if (str) {
            return str.trim();
        }
        return str;
    }
    /**
     * Returns whether or not argument is the cookie object.
     * @param arg Arguments to check.
     */
    isCookieInstance(arg) {
        return 'name' in arg;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CookieService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CookieService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

const IS_SSRENDERED_APP = new InjectionToken('IsSSRenderedApp');

// AppConfigService
/**
 * The AppConfigService provides access to the Application Config.
 */
class AppConfigService {
    constructor(_cookieService, _platformId, _document, _isSSRApp) {
        this._cookieService = _cookieService;
        this._platformId = _platformId;
        this._document = _document;
        this._isSSRApp = _isSSRApp;
        this.tenantId = 'CBOL';
        this.channelId = 'CBOL';
        let tenant = this._cookieService.getCookie('tenantId');
        this.isBrowser = isPlatformBrowser(this._platformId);
        this.setTenantId(!!tenant ? tenant : this.tenantId);
    }
    /**
     * The getLocale method gets the current locale.
     * method: getLocale
     */
    getLocale() {
        console.warn('THE LOCALE IS ' + this._appConfig.locale);
        return this._appConfig.locale;
    }
    /**
   * The getMessageConfig method gets the messaging config.
   * method: getMessageConfig
   */
    getMessageConfig() {
        return this._appConfig.messageConfig;
    }
    /**
  * The getAEMConfig method gets the AEM config.
  * method: getAEMConfig
  */
    getAEMConfig() {
        const aemConfig = this._appConfig.aemConfig;
        const { swdcAemConfig, gtdcAemConfig, ...genericConfig } = aemConfig;
        if (this._cookieService.getCookie('CITI_SITE') === 'swdc') {
            return aemConfig.swdcAemConfig ? aemConfig.swdcAemConfig : genericConfig;
        }
        else if (this._cookieService.getCookie('CITI_SITE') === 'gtdc') {
            return aemConfig.gtdcAemConfig ? aemConfig.gtdcAemConfig : genericConfig;
        }
        return genericConfig;
    }
    /**
     * The setLocale method sets the {locale}.
     * method: setLocale
     *
     * param: locale {string} The locale.
     */
    setLocale(locale) {
        this._appConfig.locale = locale;
    }
    /**
     * The getBusinessId method gets the current businessId.
     * method: getBusinessId
     */
    getBusinessId() {
        return this._appConfig.businessId;
    }
    /**
     * The setBusinessId method sets the {businessId}.
     * method setLocale
     *
     * param: businessId {string} The business id.
     */
    setBusinessId(businessId) {
        this._appConfig.businessId = businessId;
    }
    /**
     * The setacqHostName method sets the {acqHostName}.
     * method setacqHostName
     *
     * param: acqHostName {string} The ACQ CBOL hostname.
     */
    setacqHostName(acqHostName) {
        this._appConfig.acqHostName = acqHostName;
    }
    /**
     * The setcsrHostName method sets the {csrHostName}.
     * method setcsrHostName
     *
     * param: csrHostName {string} The CSR Angular CBOL hostname.
     */
    setcsrHostName(csrHostName) {
        this._appConfig.csrHostName = csrHostName;
    }
    /* adding for Cards Marketing pages*/
    setcardsmkrHostName(cardsmkrHostName) {
        this._appConfig.cardsmkrHostName = cardsmkrHostName;
    }
    /**
     * The setAppConfig method sets the {AppConfig} object containing the current configuration.
     * method: setAppConfig
     *
     * param: appConfig {AppConfig} The Application Configuration object.
     */
    setAppConfig(appConfig) {
        Object.assign(appConfig, {
            crossSiteScriptingRegex: new RegExp('\\00|<|\\+ADw-|>|\\+AD4-|%25|\\(\\s*[a-z]{2,}\\.[a-z]{2,}.*\\)', 'g')
        });
        this._appConfig = appConfig;
        this._appConfig.apiServerUrl = this.domainRealignedUrl(this._appConfig.apiServerUrl);
        if (this._appConfig.contentService &&
            this._appConfig.contentService.staticBaseUrl) {
            this._appConfig.contentService.staticBaseUrl = this.domainRealignedUrl(this._appConfig.contentService.staticBaseUrl);
        }
        this._appConfig.sessionService.sessionKeepAliveUrl = this.domainRealignedUrl(this._appConfig.sessionService.sessionKeepAliveUrl);
        if (this.isBrowser) {
            if (this._document.cookie.indexOf('partnerId') != -1) {
                this.setPartnerId(this._cookieService.getCookie('partnerId'));
            }
        }
    }
    /**
     * Set and Get methods for partnerId in appConfig object
     */
    setPartnerId(partnerId) {
        this._appConfig.partnerId = partnerId;
    }
    getPartnerId() {
        return this._appConfig.partnerId;
    }
    /**
     * The getEnvironment method gets the environment from the Application Configuration Object.
     * method: getEnvironment
     */
    getEnvironment() {
        return this._appConfig.environment;
    }
    /**
     * The getacqHostName method gets the hostname of ACQ CBOL from the Application Configuration Object.
     * method: getacqHostName
     */
    getacqHostName() {
        return this._appConfig.acqHostName;
    }
    /**
     * The getcsrHostName method gets the hostname of CSR Angular CBOL from the Application Configuration Object.
     * method: getcsrHostName
     */
    getcsrHostName() {
        return this._appConfig.csrHostName;
    }
    /* Adding for fetching the variables in the appconfig for DF1071 microapp */
    getDFMicroAppObj() {
        if (!!this._appConfig.df1071Config)
            return this._appConfig.df1071Config;
        else
            return {};
    }
    /* adding for cards marketing to get domain map*/
    getcardsmkrHostName() {
        if (!!this._appConfig.cardsmkrHostName)
            return this._appConfig.cardsmkrHostName;
        else
            return '';
    }
    /**
     * The getnewCitiHostName method gets the hostname for new citi.com domain from the Application Configuration Object.
     * method: getnewCitiHostName
     */
    getnewCitiHostName() {
        return this._appConfig.newCitiHostName;
    }
    /**
     * The getApiServerUrl method gets the apiServerUrl from the Application Configuration Object.
     * method: getApiServerUrl
     */
    getApiServerUrl() {
        if (this._isSSRApp) {
            return isPlatformServer(this._platformId)
                ? this._appConfig.apiServerUrlPlatformServer
                : this._appConfig.apiServerUrl;
        }
        return this._appConfig.apiServerUrl;
    }
    /**
     * The setApiServerUrl method sets the {apiServerUrl}.
     * method: setApiServerUrl
     *
     * param: apiServerUrl {string} The apiServerUrl.
     */
    setApiServerUrl(apiServerUrl) {
        this._appConfig.apiServerUrl = this.domainRealignedUrl(apiServerUrl);
    }
    /**
     * The crossSiteScriptingRegex method gets the apiServerUrl from the Application Configuration Object.
     * method: crossSiteScriptingRegex
     */
    crossSiteScriptingRegex() {
        return this._appConfig.crossSiteScriptingRegex;
    }
    /**
     * The getSessionCookieInformation method gets the sessioncookieInformation from the Application Configuration Object.
     * method: getSessionCookieInformation
     */
    getSessionCookieInformation() {
        return this._appConfig.sessionCookieInformation;
    }
    /**
     * The getSessionServiceConfig method gets the sessionService from the Application Configuration Object.
     * method: getSessionServiceConfig
     */
    getSessionServiceConfig() {
        return this._appConfig.sessionService;
    }
    /**
     * The getEUMAppConfig method gets the EUM Configuration from the Application Configuration Object.
     * method: getEUMAppConfig
     */
    /* getEUMAppConfig(): EUMAppConfig {
       return this._appConfig.EUMAppConfig;
     }
   */
    /**
     * The getHttpServiceConfig method gets the httpService from the Application Configuration Object.
     * method: getHttpServiceConfig
     */
    getHttpServiceConfig() {
        return this._appConfig.httpService;
    }
    getContentServiceConfig() {
        return this._appConfig.contentService;
    }
    getEntitlementFlagConfig() {
        if (!!this._appConfig.isFeatureEntitlementEnabled)
            return this._appConfig.isFeatureEntitlementEnabled;
        else
            return false;
    }
    getBrandingFlagConfig() {
        if (!!this._appConfig.newLogicBranding)
            return this._appConfig.newLogicBranding;
        else
            return false;
    }
    getMergeSSRCSR() {
        if (!!this._appConfig.mergeSSRCSR)
            return this._appConfig.mergeSSRCSR;
        else
            return false;
    }
    getSessionDeleteFlagConfig() {
        if (!!this._appConfig.newSessionDeleteLogic)
            return this._appConfig.newSessionDeleteLogic;
        else
            return false;
    }
    /**
     * The getConfig method gets the config object specified by param in the Application Configuration Object.
     * method: getConfig
     *
     * param: configObject {string} The name of the config object at root level we want to retrieve.
     */
    getConfig(configObject) {
        if (configObject) {
            return this._appConfig[configObject] || {};
        }
        else {
            return this._appConfig;
        }
    }
    getSessionIdentifiers() {
        return this._appConfig.sessionIdentifiers;
    }
    getTenantId() {
        return this.tenantId;
    }
    getChannelId() {
        return this.channelId;
    }
    setChannelId(newChannelId) {
        this.channelId = newChannelId;
        this._appConfig.httpService.defaultHeaders.channelId = this.channelId; // because http service is going to use channelId from default headers for requests
    }
    /**
     *
     * setTenantId modifies the default tenantId value in the environemnt config object
     * @param newTenantId is passed by the journey teams for identifying the mobile sessions
     */
    setTenantId(newTenantId) {
        this.tenantId = newTenantId;
        this._cookieService.setCookie({
            name: 'tenantId',
            value: newTenantId,
            path: '/',
            expires: 0.02,
            domain: 'citi.com'
        });
    }
    getApiServerUrlPlatformServer() {
        return this._appConfig.apiServerUrlPlatformServer;
    }
    setApiServerUrlPlatformServer(apiServerUrlPlatformServer) {
        this._appConfig.apiServerUrlPlatformServer = apiServerUrlPlatformServer;
    }
    domainRealignedUrl(url) {
        if (!url)
            return;
        if (!this._isSSRApp ||
            isPlatformServer(this._platformId) ||
            url.includes('api.citi.com') || //DEV environment URL
            location.origin.includes('localhost') ||
            (url.includes('online.citi.com') &&
                location.origin.includes('online.citi.com')) || //already include 'online.citi.com' in API URL
            (location.origin.includes('citi.com') &&
                !location.origin.includes('online.citi.com')) //citi.com domain
        ) {
            return url; //citi.com, //online.citi.com
        }
        url = url.replace('www.citi.com', 'citi.com');
        const index = url.indexOf('citi.com');
        const environment = url.substring(0, index).replace('0', '');
        return `${environment}online.${url.substring(index)}`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: AppConfigService, deps: [{ token: CookieService }, { token: PLATFORM_ID }, { token: DOCUMENT }, { token: IS_SSRENDERED_APP, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: AppConfigService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: AppConfigService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CookieService }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [IS_SSRENDERED_APP]
                }] }] });

class EcmCookieService {
    // Constructor
    constructor(cookieService, _platform) {
        this.cookieService = cookieService;
        this._platform = _platform;
        // This is the name value map, which maps names to indexes of the ECM cookie values
        this.cookieValueMap = {
            isCitigoldCookie: 1,
            alertEnrollnd: 2,
            autoPayEnrllnd: 3,
            BALCONId: 4,
            isBillPresentment: 5,
            isSPFMigrated: 6,
            isThankYouEnrolledinCC: 7,
            isVANEligible: 8,
            mob: 9,
            PID: 10,
            StatusCode: 11,
            isBPActive: 12,
            hasCD: 13,
            hasChecking: 14,
            hasCheckingPlus: 15,
            hasCreditCard: 16,
            hasMortgage: 17,
            hasSavings: 18,
            isBrokerage: 19,
            isCitiblue: 20,
            isCitigold: 21,
            isCPC: 22,
            isGEB: 23,
            isIPB: 24,
            isPaperless: 25,
            isPBG: 26,
            isThankYou: 27,
            isBusinessCust: 28,
            isBusinessOnly: 29,
            isCitiBizTier: 30,
            isRELOnly: 31,
            isCitiAlliance: 57,
            isCitiPriority: 32,
            isCitiGoldPrivateClientCookie: 53
        };
        // Kicks off the service by getting the ecm cookie
        this.init = () => {
            this.ecm = this.cookieService.getCookie('CBOLECM');
            // If we got an ECM cookie
            if (this.ecm) {
                // Split it on the '-' delimiter
                this.ecm = this.ecm.split('-');
            }
            // Set whether or not we got an ecm cookie
            this.hasEcmCookie = this.ecm ? true : false;
        };
        // Gets an ECM cookie value by name
        this.getValueByName = name => {
            if (isPlatformServer(this._platform)) {
                return false;
            }
            // If the service has an ECM cookie
            if (this.hasEcmCookie) {
                // If name is a string value and has length and the name matches a value in the cookieValueMap object
                if (typeof name === 'string' &&
                    name.length &&
                    typeof this.cookieValueMap[name] !== 'undefined') {
                    // Use the index provided by the cookieValueMap to return the correct ECM cookie value
                    return this.ecm[this.cookieValueMap[name] - 1];
                    // Otherwise if we are in dev mode
                }
                /* else if (isDevMode) {
                  // Warn that the value passed was not a string, or the name was invalid.  Also return a list of valid names.
                  console.warn(
                    'EcmCookeService - getValueByName: You provided a non-string name or the name you passed was not valid. Valid names are: ',
                    this.cookieValueMap
                  );
                } */
                return false;
                // Otherwise if we didn't find an ECM cookie
            }
            /*
            else if (isDevMode) {
              // Let the user know
              console.warn('The ECM cookie service did not find an ECM cookie.');
            } */
            return false;
        };
        // Get an ECM cookie value by index (if you know the index of the value in the cookie you are looking for)
        this.getValueByIndex = index => {
            if (isPlatformServer(this._platform)) {
                return false;
            }
            // If the service has an ECM cookie
            if (this.hasEcmCookie) {
                // If the index we got was a number, and there is a value in the ECM cookie at the index provided
                if (typeof index === 'number' && typeof this.ecm[index] !== 'undefined') {
                    return this.ecm[index + 1];
                }
                // Otherwise if we didn't find an ECM cookie
            }
            /* else if (isDevMode) {
              // Let the user know
             // console.warn('The ECM cookie service did not find an ECM cookie.');
            } */
        };
        // Kicks off the service
        this.init();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: EcmCookieService, deps: [{ token: CookieService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: EcmCookieService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: EcmCookieService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CookieService }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

// FeatureDetection
class FeatureDetectionService {
    constructor() {
        this._navigator = {};
    }
    /*
    -----------------------------------------------------------------------------------
        [detectFeature(detectionFeature): any {}]
        checks if the particular features are available in the client's machine or not
        call this method as detectFeature('stringYouWantToDetect')
        Currently, we checks for the following features
        CANVAS, LOCALSTORAGE, SESSIONSTORAGE, GEOLOCATION, SVG, WEBSOCKET
    -----------------------------------------------------------------------------------
    */
    detectFeature(detectionFeature) {
        switch (detectionFeature.toLowerCase()) {
            case 'canvas': {
                return !!document.createElement('canvas').getContext ? true : false;
            }
            case 'localstorage': {
                return 'localStorage' in window && window['localStorage'] !== null
                    ? true
                    : false;
            }
            case 'sessionstorage': {
                return 'sessionStorage' in window && window['sessionStorage'] !== null
                    ? true
                    : false;
            }
            case 'geolocation': {
                return 'geolocation' in navigator ? true : false;
            }
            case 'svg': {
                return document.implementation.hasFeature('http://www.w3.org/TR/SVG11/feature#Image', '1.1')
                    ? true
                    : false;
            }
            case 'websocket': {
                return typeof WebSocket === 'function' ? true : false;
            }
            default: {
                return 'unknown';
            }
        }
    }
    // Returns either the client is on mobile device or not
    isMobile() {
        let isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        return isMobile ? true : false;
    }
    // This will return the iPhone models as an string
    getIPhoneModel() {
        if (this.isMobile()) {
            // iPhone 6 Plus
            if (window.screen.height / window.screen.width == 736 / 414 &&
                window.devicePixelRatio == 3) {
                return 'iPhone 6 Plus';
            }
            // iPhone 6
            else if (window.screen.height / window.screen.width == 667 / 375 &&
                window.devicePixelRatio == 2) {
                return 'iPhone 6';
            }
            // iPhone 5/5C/5S or 6 in zoom mode
            else if (window.screen.height / window.screen.width == 1.775 &&
                window.devicePixelRatio == 2) {
                return 'iPhone 5, 5C, 5S or 6 (display zoom)';
            }
            // iPhone 4/4S
            else if (window.screen.height / window.screen.width == 1.5 &&
                window.devicePixelRatio == 2) {
                return 'iPhone 4 or 4S';
            }
            // iPhone 1/3G/3GS
            else if (window.screen.height / window.screen.width == 1.5 &&
                window.devicePixelRatio == 1) {
                return 'iPhone 1, 3G or 3GS';
            }
            else {
                return 'Not an iPhone';
            }
        }
        else {
            return 'Not a mobile device';
        }
    }
    /*
    -----------------------------------------------------------------------------------
        If no parameters are passed, it will return the entire navigator object
        If called with parameters, it will return the value(s) of that key
        Example of calling with parameters:
        getNavigatorInfo('userAgent'); This will return the userAgent string something like this: 'Mozilla/5.0 (Windows NT 6.1; WOW64; rv:49.0) Gecko/20100101 Firefox/49.0'
    -----------------------------------------------------------------------------------
    */
    getNavigatorInfo(navigatorType) {
        // tslint:disable-next-line:forin
        for (let i in navigator) {
            this._navigator[i] = navigator[i];
        }
        if (navigatorType) {
            return this._navigator[navigatorType];
        }
        else {
            return JSON.stringify(this._navigator);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: FeatureDetectionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: FeatureDetectionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: FeatureDetectionService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

/** Storage service **/
const preDefinedregex = /\00|<|\+ADw-|>|\+AD4-|%25|System[.][a-z]|javascript\s*:|['"].*[+\-*/%=&|^~'"]|\?.*:|\(\s*[a-z]{2,}\.[a-z]{2,}.*\)/g;
class StorageService {
    static { this.COOKIE = 'cookie'; }
    static { this.LOCALSTORAGE = 'localstorage'; }
    static { this.SESSIONSTORAGE = 'sessionstorage'; }
    constructor(_appConfigService, storage, sessionStorage, featureDetectionService) {
        this._appConfigService = _appConfigService;
        this.storage = storage;
        this.sessionStorage = sessionStorage;
        this.featureDetectionService = featureDetectionService;
        this.SECERET_KEY = 'asdfgh0987654321';
        this.appConfigRegex = _appConfigService.crossSiteScriptingRegex();
    }
    /**
     * The set method returns a boolean after inserting the key,value inside the cookie or Storage.
     * method: set
     *
     * param: key: string - Accepts the key for storing in Cookie or Storage
     * param: Value: string - Accepts the value for the key for storing in Cookie or Storage
     * param: destination: string - Would accept any of these values(all in lower case)
     *          cookie
     *          localstorage
     *          sessionstorage
     * param: cipher: boolean -This is an optional paarameter that needs to be set to true if the value needs to be encrypted before setting . Default is false .
     * param: CookieExpires: number - This is an optional parameter that needs to be sent only if the destination is cookie.Cookie's expiration date in days from now. If it's undefined the cookie is a session Cookie
     * param: path: string - This is an optional parameter that needs to be sent only if the destination is cookie.Path relative to the domain where the cookie should be avaiable. Default '/'
     */
    set(key, value, destination, cipher, CookieExpires, path) {
        let destinationValue = destination != null && destination != ''
            ? destination.toLocaleLowerCase()
            : null;
        let path1 = path != null && path != '' ? path : '/';
        cipher =
            cipher == null || cipher == undefined || cipher == false ? false : true;
        let valueCheck = this.checkForUnexpectedPattern([value]);
        //Encrypting
        value = this.encrypt(value, cipher);
        let localStorageEnabled = this.featureDetectionService.detectFeature('localStorage');
        let sessionStorageEnabled = this.featureDetectionService.detectFeature('sessionStorage');
        if (valueCheck != false) {
            try {
                if (destination != null && destination != '') {
                    switch (destinationValue) {
                        case StorageService.COOKIE:
                            if (key != null && key != '' && value != null) {
                                Cookie$1.set(key, value, CookieExpires, path1);
                                return true;
                            }
                            else {
                                return false;
                            }
                        case StorageService.LOCALSTORAGE:
                            if (key != null &&
                                key != '' &&
                                value != null &&
                                localStorageEnabled) {
                                this.storage.store(key, value);
                                return true;
                            }
                            else {
                                return false;
                            }
                        case StorageService.SESSIONSTORAGE:
                            if (key != null &&
                                key != '' &&
                                value != null &&
                                sessionStorageEnabled) {
                                this.sessionStorage.store(key, value);
                                return true;
                            }
                            else {
                                return false;
                            }
                        default:
                            return false;
                    }
                }
                else {
                    switch (true) {
                        case sessionStorageEnabled &&
                            key != null &&
                            key != '' &&
                            value != null:
                            this.sessionStorage.store(key, value);
                            return true;
                        case localStorageEnabled &&
                            key != null &&
                            key != '' &&
                            value != null:
                            this.storage.store(key, value);
                            return true;
                        case key != null && key != '' && value != null:
                            Cookie$1.set(key, value, CookieExpires, path1);
                            return true;
                        default:
                            return false;
                    }
                }
            }
            catch (Exception) {
                return false;
            }
        }
        else {
            return false;
        }
    }
    /**
     * The get method returns string if key is available in cookie or local or session Storage. Cookie takes precedence.
     * method: get
     *
     * param: key: string - Accepts the key for storing in Cookie or Storage
     * param: destination: string - Would accept any of these values(all in lower case)
     *          cookie
     *          localstorage
     *          sessionstorage
     * param: cipher: boolean -This is an optional paarameter that needs to be set to true if the value needs to be encrypted before setting . Default is false .
     */
    get(key, destination, cipher) {
        cipher =
            cipher == null || cipher == undefined || cipher == false ? false : true;
        let arrayValues = this.getArray(key, null, destination, cipher);
        if (arrayValues != null && arrayValues.length > 0) {
            let val = arrayValues[0];
            if (key === 'locale') { // DRR-13706 temp fix to ignore locale values after 'en/es_US'
                val = val.includes('es_US') ? 'es_US' : 'en_US';
            }
            return val;
        }
        return null;
    }
    /**
     * The get method returns string[] if key is available in cookie or local or session Storage. Cookie takes precedence.
     * method: get
     *
     * param: key: string - Accepts the key for storing in Cookie or Storage
     * param: seperator: string - Accepts the value seperator if present
     * param: destination: string - Would accept any of these values(all in lower case)
     *          cookie
     *          localstorage
     *          sessionstorage
     * param: cipher: boolean -This is an optional paarameter that needs to be set to true if the value needs to be encrypted before setting . Default is false .
     */
    getArray(key, seperator, destination, cipher) {
        let outputStringArray = null;
        let localStorageEnabled = this.featureDetectionService.detectFeature('localStorage');
        let sessionStorageEnabled = this.featureDetectionService.detectFeature('sessionStorage');
        cipher =
            cipher == null || cipher == undefined || cipher == false ? false : true;
        if (destination != null && destination != '') {
            switch (destination) {
                case StorageService.COOKIE:
                    let tempValue = this.decrypt(Cookie$1.get(key), cipher);
                    if (seperator != null) {
                        outputStringArray = tempValue ? tempValue.split(seperator) : null;
                    }
                    else {
                        outputStringArray = tempValue ? [tempValue] : null;
                    }
                    break;
                case StorageService.LOCALSTORAGE:
                    if (localStorageEnabled) {
                        let tempValue = this.decrypt(this.storage.retrieve(key), cipher);
                        if (seperator != null) {
                            outputStringArray = tempValue ? tempValue.split(seperator) : null;
                        }
                        else {
                            outputStringArray = tempValue ? [tempValue] : null;
                        }
                    }
                    else {
                        outputStringArray = null;
                    }
                    break;
                case StorageService.SESSIONSTORAGE:
                    if (sessionStorageEnabled) {
                        let tempValue = this.decrypt(this.sessionStorage.retrieve(key), cipher);
                        if (seperator != null) {
                            outputStringArray = tempValue ? tempValue.split(seperator) : null;
                        }
                        else {
                            outputStringArray = tempValue ? [tempValue] : null;
                        }
                    }
                    else {
                        outputStringArray = null;
                    }
                    break;
                default:
                    return null;
            }
        }
        else {
            switch (false) {
                case !Cookie$1.get(key):
                    let retValue = this.decrypt(Cookie$1.get(key), cipher);
                    if (seperator != null) {
                        outputStringArray = retValue ? retValue.split(seperator) : null;
                    }
                    else {
                        outputStringArray = retValue ? [retValue] : null;
                    }
                    break;
                case !this.storage.retrieve(key):
                    if (localStorageEnabled) {
                        let tempValue = this.decrypt(this.storage.retrieve(key), cipher);
                        if (seperator != null) {
                            outputStringArray = tempValue ? tempValue.split(seperator) : null;
                        }
                        else {
                            outputStringArray = tempValue ? [tempValue] : null;
                        }
                    }
                    else {
                        outputStringArray = null;
                    }
                    break;
                case !this.sessionStorage.retrieve(key):
                    if (sessionStorageEnabled) {
                        let tempValue = this.decrypt(this.sessionStorage.retrieve(key), cipher);
                        if (seperator != null) {
                            outputStringArray = tempValue ? tempValue.split(seperator) : null;
                        }
                        else {
                            outputStringArray = tempValue ? [tempValue] : null;
                        }
                    }
                    else {
                        outputStringArray = null;
                    }
                    break;
                default:
                    break;
            }
        }
        if (null == outputStringArray) {
            return null;
        }
        else {
            return this.checkForUnexpectedPattern(outputStringArray)
                ? outputStringArray
                : null;
        }
    }
    /**
     * The delete method returns boolean if keyis available in cookie or local or session Storage is deleted. Cookie takes precedence.
     * method: deleteCookieOrStorageValue
     *
     * param: key: string - Accepts the key for storing in Cookie or Storage
     * param: destination: string - Would accept any of these values(all in lower case)
     *          cookie
     *          localstorage
     *          sessionstorage
     * param: path: string - Thsi optional parameter to be sent only for cookie if it is stored in a diff path
     */
    delete(key, destination, path) {
        let returnValue = false;
        let path1 = path != null && path != '' ? path : '/';
        let localStorageEnabled = this.featureDetectionService.detectFeature('localStorage');
        let sessionStorageEnabled = this.featureDetectionService.detectFeature('sessionStorage');
        if (destination != null && destination != '') {
            switch (destination) {
                case StorageService.COOKIE:
                    Cookie$1.delete(key, path1);
                    return true;
                case StorageService.LOCALSTORAGE:
                    if (localStorageEnabled)
                        this.storage.clear(key);
                    return localStorageEnabled;
                case StorageService.SESSIONSTORAGE:
                    if (sessionStorageEnabled)
                        this.sessionStorage.clear(key);
                    return sessionStorageEnabled;
                default:
                    return false;
            }
        }
        else {
            switch (false) {
                case !Cookie$1.get(key):
                    Cookie$1.delete(key);
                    return true;
                case !this.storage.retrieve(key):
                    if (localStorageEnabled)
                        this.storage.clear(key);
                    return localStorageEnabled;
                case !this.sessionStorage.retrieve(key):
                    if (sessionStorageEnabled)
                        this.sessionStorage.clear(key);
                    return sessionStorageEnabled;
                default:
                    break;
            }
        }
        return returnValue;
    }
    /**
     * The checkForUnexpectedPattern method returns boolean if the value contains unexpected pattern which might lead to CSRF threat
     * method: checkForUnexpectedPattern
     *
     * param: valueArray: string[] - Accepts the value that needs to be checked before inserting
     * It also has a regex which would be taken as an input from app-config else the default would be the below one(hardcoded in this file like below)
     * const preDefinedregex = /\00|<|\+ADw-|>|\+AD4-|%25|System[.][a-z]|javascript\s*:|[''].*[+\-*\/%=&|^~'']|\?.*:|\(\s*[a-z]{2,}\.[a-z]{2,}.*\)/g;
     */
    checkForUnexpectedPattern(valueArray) {
        let m;
        let returnValue = true;
        const regex = this.appConfigRegex != null && this.appConfigRegex != ''
            ? this.appConfigRegex
            : preDefinedregex;
        // Please use for loop instead of forEach. 
        // forEach loop is returning true if catch block executed. Expected output should be false.
        // for loop will return false if catch block executed which is expected output.
        valueArray.forEach(value => {
            try {
                // tslint:disable-next-line:no-conditional-assignment
                while ((m = regex.exec(value)) !== null) {
                    // This is necessary to avoid infinite loops with zero-width matches
                    if (m.index === regex.lastIndex) {
                        regex.lastIndex++;
                    }
                    // The result can be accessed through the `m`-variable.
                    m.forEach((match, groupIndex) => {
                        returnValue = false;
                    });
                }
            }
            catch (Exception) {
                return false;
            }
        });
        return returnValue;
    }
    /**
     * The encrypt method returns the value with or without encypting based on input.It uses crypto-JS API
     * method: encrypt
     *
     * param: value: string - Accepts the value that needs to be encrypted
     * param: cipher: boolean - Accepts an boolean which determines whether the value needs to be encrypted or not. Private method . So default is false
     */
    encrypt(value, cipher) {
        let returnValue;
        try {
            if (cipher) {
                let bytes = encrypt(value, this.SECERET_KEY);
                returnValue = bytes != null ? bytes.toString() : null;
            }
            else {
                returnValue = value;
            }
        }
        catch (Exception) {
            returnValue = value;
        }
        return returnValue;
    }
    /**
     * The decrypt method returns the value with or without decypting based on input.It uses crypto-JS API
     * method: decrypt
     *
     * param: value: string - Accepts the value that needs to be decrypted
     * param: cipher: boolean - Accepts an boolean which determines whether the value needs to be decrypted or not. Private method . So default is false
     */
    decrypt(value, cipher) {
        let returnValue;
        try {
            if (cipher) {
                let bytes = decrypt(value, this.SECERET_KEY);
                returnValue = bytes != null ? bytes.toString(utf8) : null;
            }
            else {
                returnValue = value;
            }
        }
        catch (Exception) {
            returnValue = value;
        }
        return returnValue;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: StorageService, deps: [{ token: AppConfigService }, { token: i2.LocalStorageService }, { token: i2.SessionStorageService }, { token: FeatureDetectionService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: StorageService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: StorageService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AppConfigService }, { type: i2.LocalStorageService }, { type: i2.SessionStorageService }, { type: FeatureDetectionService }] });

// SessionService
class SessionService {
    constructor(_appConfigService, _storageService, _httpClient) {
        this._appConfigService = _appConfigService;
        this._storageService = _storageService;
        this._httpClient = _httpClient;
    }
    isPresentFriendKey() {
        let isFriendKey = this._appConfigService.getSessionCookieInformation()
            .isFriendKey;
        let isFriend = this._storageService.get(isFriendKey);
        return isFriend !== null && isFriend !== 'false'; // TODO:  Friend key is never === 'false' as its values are 'Y' and 'N'
    }
    /**
     * The isVisitor method returns true if the cookie for the session does not exist or it exists but it does not contain the session tokens.
     * method: isVisitor
     */
    isVisitor() {
        return this.getSessionType() == null ? true : false;
    }
    /**
     * The isCustomer method returns true if all the OUath token is present for the customer. If OuathToken is not present then it would return false.
     * method: isCustomer
     */
    isCustomer() {
        return !!this.getSessionType() && !this.isPresentFriendKey();
    }
    /**
     * The isFriend method returns true if all the OUath token is present and also if the key for isFriend is available in storage
     * method: isFriend
     */
    isFriend() {
        return !!this.getSessionType() && this.isPresentFriendKey();
    }
    /**
     * The sessionType method returns type of session the customer has - (nga or legacy)
     * If there is no session available, it returns null
     * method: sessionType
     */
    getSessionType() {
        return this._sessionType;
    }
    setSessionType(val) {
        this._sessionType = val;
    }
    /**
     * The setFriendKey method inserts the friend key in the storage.
     * method: setFriendKey
     *
     * param: isFriend {boolean} If true it inserts the friend key, if false it deletes the friend key.
     */
    setFriendKey(isFriend) {
        let isFriendKey = this._appConfigService.getSessionCookieInformation()
            .isFriendKey;
        if (isFriend) {
            this._storageService.set(isFriendKey, 'true');
        }
        else {
            this._storageService.delete(isFriendKey);
        }
    }
    revokeAccessToken() {
        let _this = this;
        return new Promise((response, reject) => {
            let jwtResponse = _this.getKoreData('jwtResponse');
            let sessionId = _this.getKoreData('convSid');
            let url = `${_this.getKoreData('revoke_token_api')}`;
            if (jwtResponse && sessionId && url) {
                let auth = jwtResponse.auth;
                let header = new HttpHeaders()
                    .set("Content-Type", "application/json")
                    .set("Auth", `bearer ${auth.accessToken}`)
                    .set("sessionId", sessionId);
                _this._httpClient.post(url, {}, { headers: header }).subscribe({
                    next: (res) => {
                        response(res);
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
            }
            else {
                reject("No Auth Token Found");
            }
        });
    }
    getKoreData(type) {
        try {
            let chatData;
            if (sessionStorage !== undefined && sessionStorage !== null) {
                chatData = JSON.parse(sessionStorage.getItem("chatData"));
                if (type == 'revoke_token_api') {
                    let kore_data = chatData['kore_data'];
                    let current_account_config = kore_data.messagingApi;
                    let service_url = current_account_config['revoke_token_api'];
                    return service_url;
                }
                else {
                    let koreData = chatData[type];
                    return koreData;
                }
            }
            else {
                return null;
            }
        }
        catch (e) {
            return null;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SessionService, deps: [{ token: AppConfigService }, { token: StorageService }, { token: i3.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SessionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SessionService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AppConfigService }, { type: StorageService }, { type: i3.HttpClient }] });

class Personalization {
    constructor() {
        // Below fields are extracted from ECM Cookie.
        this.isSPFMigrated = false;
        this.hasChecking = false;
        this.hasCheckingPlus = false;
        this.hasCreditCard = false;
        this.hasMortgage = false;
        this.hasSavings = false;
        this.isBrokerage = false;
        this.isCitiblue = true;
        this.isCitigold = false;
        this.isCitipriority = false;
        this.isRELOnlyUser = false;
        this.isPBG = false;
        // Below fields are extracted from session storage
        this.isMember = false;
        this.isFriend = false;
        this.isVisitor = true;
        this.isAACard = false;
        this.isHiltonCard = false;
        this.hasAAdvantageCard = false;
        this.language = 'en';
        this.isInvestor = false;
        this.isBanker = false;
        this.isATTCard = false;
        this.hasAAdvantageCardNew = false;
        this.isNewATnTPID = false;
        this.isCinlessUser = false;
        this.isBusinessOnly = false;
        this.customerType = '';
        this.isCitiPrivateBanking = false;
        this.packageInd = '0';
        this.residentialState = '';
        this.isGEB = false;
        this.isIPB = false;
        this.isCPC = false;
    }
}
class User {
    constructor() {
        this.personalization = new Personalization();
    }
}

class UserPersonalizationService extends EcmCookieService {
    constructor(_cookieService, _window, _platformId) {
        super(_cookieService, _platformId);
        this._cookieService = _cookieService;
        this._window = _window;
        this._platformId = _platformId;
        this.user = new User();
        this.initPersonlizationData();
    }
    initPersonlizationData() {
        let sessionParams = this._window
            ? JSON.parse(this._window.sessionStorage.getItem('sessionPostBrandingStorage'))
            : null;
        if (sessionParams == null) {
            this.getUserPrefFromCookie();
        }
        else {
            this.getUserPrefFromSessionStorage();
        }
    }
    getUserPrefFromCookie() {
        let propVal = '';
        for (const prop of Object.keys(this.user.personalization)) {
            propVal = this.getValueByName(prop);
            if (propVal) {
                if (propVal === 'Y') {
                    this.user.personalization[prop] = true;
                }
                else if (propVal === 'N') {
                    this.user.personalization[prop] = false;
                }
                else {
                    this.user.personalization[prop] = propVal;
                }
            }
        }
    }
    getUserPrefFromSessionStorage() {
        let sessionParams = this._window
            ? this._window.sessionStorage.getItem('sessionPostBrandingStorage')
            : null;
        let propVal = '';
        if (sessionParams !== null) {
            try {
                sessionParams = JSON.parse(sessionParams);
                for (const prop of Object.keys(this.user.personalization)) {
                    if (sessionParams !== null && sessionParams[prop]) {
                        propVal = sessionParams[prop];
                        if (propVal) {
                            if (propVal === 'Y') {
                                this.user.personalization[prop] = true;
                            }
                            else if (propVal === 'N' || propVal == 'null') {
                                this.user.personalization[prop] = false;
                            }
                            else {
                                this.user.personalization[prop] = propVal;
                            }
                        }
                    }
                }
            }
            catch (e) { }
        }
    }
    getPersonalizationData() {
        return this.user;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: UserPersonalizationService, deps: [{ token: CookieService }, { token: WINDOW }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: UserPersonalizationService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: UserPersonalizationService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CookieService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

const NAVIGATOR = new InjectionToken('NavigatorToken');
/* Define abstract class for obtaining reference to the global Navigator object. */
class NavigatorRef {
    get nativeNavigator() {
        throw new Error('Not implemented.');
    }
}
/* Define class that implements the abstract class and returns the native Navigator object. */
class BrowserNavigatorRef extends NavigatorRef {
    constructor(platformId) {
        super();
        this.platformId = platformId;
    }
    get nativeNavigator() {
        if (isPlatformBrowser(this.platformId)) {
            return navigator;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserNavigatorRef, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserNavigatorRef, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserNavigatorRef, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });
/* Create an factory function that returns the native Navigator object. */
function navigatorFactory(browserNavigatorRef, platformId) {
    if (isPlatformBrowser(platformId)) {
        return browserNavigatorRef.nativeNavigator;
    }
    return null;
}
const browserNavigatorProvider = {
    provide: NavigatorRef,
    useClass: BrowserNavigatorRef
};
/* Create an injectable provider that uses the NavigatorFactory function for returning the native Navigator object. */
const NavigatorProvider = {
    provide: NAVIGATOR,
    useFactory: navigatorFactory,
    deps: [NavigatorRef, PLATFORM_ID]
};
/* Create an array of providers. */
const NAVIGATOR_PROVIDER = [browserNavigatorProvider, NavigatorProvider];

class PostSessionService {
    // Constructor
    constructor(_window) {
        this._window = _window;
        // Whether or not the sessionStorageNotFoundError has been emitted yet or not
        this.storageErrorEmitted = false;
        // Warning to show if the session storage is not found or errors out as invalid JSON
        this.storageNotFoundError = 'Post Session Service: No session storage object found or session storage object was invalid JSON. Please check that sessionPostBrandingStorage exists in session storage and is valid JSON.';
        // Warning to show if the item requested was not found in session storage
        this.itemNotFoundError = 'Post Session Service: The post session storage item you requested does not exist in the session storage object.';
        this.init();
    }
    init() {
        if (!(this._window && this._window.sessionStorage))
            return;
        // Check for a session post branding storage object in session storage
        let sessionStorage = this._window.sessionStorage.getItem('sessionPostBrandingStorage');
        // If we found a session storage object
        if (sessionStorage) {
            // See if we can eval the object into JSON
            try {
                // Try to parse the session storage object
                sessionStorage = JSON.parse(sessionStorage);
                // If we found a session storage object
                if (sessionStorage) {
                    // Set it on the service
                    this.sessionStorage = sessionStorage;
                }
            }
            catch (err) {
                // If we are in dev mode
                if (isDevMode && !this.storageErrorEmitted) {
                    // Warn that we did not find a session storage object
                    //   console.warn(this.storageNotFoundError);
                    this.storageErrorEmitted = true;
                }
            }
        }
    }
    // Gets the full session storage object
    getSessionStorageObject() {
        return this.sessionStorage;
    }
    // Gets an item from session storage by name
    getItem(itemName) {
        // Check if we have a session storage object and the item exists
        let item = this.sessionStorage &&
            typeof this.sessionStorage[itemName] !== 'undefined'
            ? this.sessionStorage[itemName]
            : false;
        // If the name request is defined in the session storage object
        if (this.sessionStorage &&
            typeof this.sessionStorage[itemName] !== 'undefined') {
            // Return the item
            return this.sessionStorage[itemName];
        }
        else {
            // If this is dev mode
            if (isDevMode) {
                // If there isnt a session storage item
                if (!this.sessionStorage && !this.storageErrorEmitted) {
                    //   console.warn(this.storageNotFoundError);
                    this.storageErrorEmitted = true;
                    // If the request item does not exist
                }
                else if (!item && !this.storageErrorEmitted) {
                    //  console.warn(this.itemNotFoundError, 'Item Requested:', itemName);
                }
            }
            // Return false
            return false;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: PostSessionService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: PostSessionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: PostSessionService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

class JSLoaderService {
    constructor(_document) {
        this._document = _document;
    }
    /* Dynamically load a JS file by creating a HTML element and appending it after the body */
    loadJS(url) {
        let node = this._document.createElement('script');
        node.src = url;
        node.type = 'text/javascript';
        node.async = true;
        node.charset = 'utf-8';
        this._document.getElementsByTagName('body')[0].appendChild(node);
    }
    /* Dynamically load a JS file and add a callback for after it is loaded */
    loadJSCallback(url, callback) {
        // Create the script tag and add it to the document
        const slib = this._document.createElement('script');
        slib.type = 'text/javascript';
        slib.async = true;
        slib.src = url;
        let cdone = false;
        const head = this._document.getElementsByTagName('head')[0];
        slib.onload = slib.onreadystatechange = function () {
            if (!cdone &&
                (!this.readyState ||
                    this.readyState === 'loaded' ||
                    this.readyState === 'complete')) {
                cdone = true;
                if (callback) {
                    callback();
                }
                // Handle memory leak in IE
                slib.onload = slib.onreadystatechange = null;
                if (head && slib.parentNode) {
                    head.removeChild(slib);
                }
            }
        };
        if (callback) {
            slib.onload = callback;
        }
        const s = this._document.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(slib, s);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: JSLoaderService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: JSLoaderService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: JSLoaderService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class ScriptLoadService {
    // Constructor
    constructor(_document) {
        this._document = _document;
        // Variables
        this.scripts = [];
    }
    // Loads a script from a url
    loadScript(url) {
        // Return the promise we will resolve
        return new Promise((resolve, reject) => {
            // Check if the script has already been loaded by this service
            if (this.scripts.includes(url)) {
                // Resolve as already loaded if it has
                resolve({ script: name, loaded: true, status: 'Already Loaded' });
            }
            else {
                // Create the script element
                let script = this._document.createElement('script');
                script.type = 'text/javascript';
                script.src = url;
                // Onload for IE
                if (script.readyState) {
                    // On change of the ready state
                    script.onreadystatechange = () => {
                        // If the state was loaded or complete
                        if (script.readyState === 'loaded' ||
                            script.readyState === 'complete') {
                            // Remove the ready state change listener
                            script.onreadystatechange = null;
                            // Update the service so it knows this script has already been loaded
                            this.scripts.push(url);
                            // Resolve the promise to let the requester know their script was loaded
                            resolve({ script: url, loaded: true, status: 'Loaded' });
                        }
                    };
                }
                else {
                    // Listen for the onload event for the new script tag
                    script.onload = () => {
                        /// Update the service so it knows this script has already been loaded
                        this.scripts.push(url);
                        // Resolve the promise to let the requester know their script was loaded
                        resolve({ script: name, loaded: true, status: 'Loaded' });
                    };
                }
                // Catch any errors loading the script and report them back to the requester by resolving the promise
                script.onerror = (error) => resolve({ script: name, loaded: false, status: 'Load Failed' });
                // Append the new script to the head tag to kick everything off
                this._document.getElementsByTagName('head')[0].appendChild(script);
            }
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: ScriptLoadService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: ScriptLoadService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: ScriptLoadService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class IdleTimeRouterComponent {
    constructor(_storageService, _appConfigService, _activatedRoute) {
        this._storageService = _storageService;
        this._appConfigService = _appConfigService;
        this._activatedRoute = _activatedRoute;
        this.userActive = false;
        this.routeToOrigin = null;
        // this._storageService.set('routeToOrigin','TY',StorageService.SESSIONSTORAGE);
    }
    ngOnInit() {
        // Get the routeToOrigin attribute from the sesion storage
        this.routeToOrigin = this._storageService.get('routeToOrigin', StorageService.SESSIONSTORAGE);
        // if routeToOrigin attribute is not available in the session storage fetch it from query param
        if (!this.routeToOrigin) {
            this.routeToOrigin = this._activatedRoute.snapshot.queryParams['routeToOrigin'];
        }
        if (this.routeToOrigin) {
            // Attaching click event listener
            document.addEventListener('click', () => {
                // flag is made to true if there are any click events on the screen
                this.userActive = true;
            });
            // Fetch the routeAfter time from the configuration file
            let idleTimeRouterConfig = this._appConfigService.getConfig('idleTimeRouterService');
            if (idleTimeRouterConfig) {
                let routeAfter = idleTimeRouterConfig.routeAfter * 1000;
                // observerable timer that observers  specified interval
                this._IdleTimeObserverable = timer(routeAfter, routeAfter);
                let self = this;
                this._IdleTimeSubscription = this._IdleTimeObserverable.subscribe(this._IdleTimeObserver);
                // Define oberver
                this._IdleTimeObserver = {
                    next: x => {
                        // if the user is not active route the user to the origin page
                        if (!this.userActive) {
                            self.routeToOriginApp();
                        }
                        // reset the active flag for next observation
                        this.userActive = false;
                    },
                    error: err => {
                        console.log('idle Time observer error');
                    },
                    complete: () => {
                        console.log('idle Time observer complete');
                    }
                };
                // subscribe to the idle time observable
            }
            else {
                console.log('idle Time observer not configured');
            }
        }
        else {
            console.log('idle Time observer not configured');
        }
    }
    /*
       This function routes the user to the origin application
    */
    routeToOriginApp() {
        // subscribe
        this._IdleTimeSubscription.unsubscribe();
        let routeUrl = null;
        // read the routing configuration from the app config
        let idleTimeRouterConfig = this._appConfigService.getConfig('idleTimeRouterService');
        let routingInfo = idleTimeRouterConfig.routingInfo;
        // Check if route to origin value is valid
        if (this.routeToOrigin) {
            let i = 0;
            // iterate through the routing info object to find the matching app name to get the route url
            if (routingInfo) {
                for (i = 0; i < routingInfo.length; i++) {
                    if (routingInfo[i]) {
                        if (this.routeToOrigin == routingInfo[i].appName) {
                            routeUrl = routingInfo[i].routeUrl;
                        }
                    }
                }
            }
            // if no route url matches, redirect to default route
            if (!routeUrl) {
                routeUrl = idleTimeRouterConfig.defaultRoute;
            }
            window.location.href = routeUrl;
        }
        else {
            console.log('No route to Origin is present');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: IdleTimeRouterComponent, deps: [{ token: StorageService }, { token: AppConfigService }, { token: i3$1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: IdleTimeRouterComponent, selector: "idleTime-router", ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: IdleTimeRouterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'idleTime-router',
                    template: ``
                }]
        }], ctorParameters: () => [{ type: StorageService }, { type: AppConfigService }, { type: i3$1.ActivatedRoute }] });

class IdleTimeRouterModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: IdleTimeRouterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: IdleTimeRouterModule, declarations: [IdleTimeRouterComponent], exports: [IdleTimeRouterComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: IdleTimeRouterModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: IdleTimeRouterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [IdleTimeRouterComponent],
                    exports: [IdleTimeRouterComponent]
                }]
        }] });

class RouterRedirectGuard {
    constructor(platformId, _appConfig) {
        this.platformId = platformId;
        this._appConfig = _appConfig;
    }
    canActivate(childRoute, state) {
        const isBrowser = isPlatformBrowser(this.platformId);
        if (!isBrowser)
            return true;
        window.location.href = window.location.href.includes('/US/ag')
            ? window.location.href.replace('/US/ag', '')
            : this._appConfig.getcsrHostName() + `/US/ag${state.url.toString()}`;
        return false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: RouterRedirectGuard, deps: [{ token: PLATFORM_ID }, { token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: RouterRedirectGuard }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: RouterRedirectGuard, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: AppConfigService }] });

class CitiAppConfigService {
    constructor(_injector) {
        this._injector = _injector;
    }
    getConfiguration(className) {
        // tslint:disable-next-line: deprecation
        const oIConfigService = this._injector.get(className);
        return oIConfigService.getConfig();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CitiAppConfigService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CitiAppConfigService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CitiAppConfigService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.Injector }] });

class CitiAppConfig {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CitiAppConfig, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CitiAppConfig }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CitiAppConfig, providers: [CitiAppConfigService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CitiAppConfig, decorators: [{
            type: NgModule,
            args: [{
                    providers: [CitiAppConfigService]
                }]
        }] });

class LoggingService {
    constructor(appConfig) {
        this.appConfig = appConfig;
        this.loggingEnabled = this.loggingEligible();
    }
    log(...args) {
        if (this.loggingEnabled) {
            console.log(args);
        }
    }
    loggingEligible() {
        if (this.appConfig.getEnvironment() != 'PROD' && !!console) {
            return true;
        }
        return false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: LoggingService, deps: [{ token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: LoggingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: LoggingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: AppConfigService }] });

// cbol-core DataCollectorService can able to share the data through out the applciation
class DataCollectorService {
    constructor() {
        this.dataCollector = {};
    }
    // add data if its type is an object
    addDataCollector(data) {
        if (data instanceof Object) {
            Object.assign(this.dataCollector, data);
        }
    }
    // get data Object
    getDataCollector() {
        return this.dataCollector;
    }
    // reset data Object
    resetDataCollector() {
        this.dataCollector = {};
    }
    // remove data based on the key string
    removeDataCollector(key) {
        if (key !== null && key !== undefined) {
            return delete this.dataCollector[key];
        }
        return undefined;
    }
    // get data based on the key string
    getDataForKey(key) {
        if (key !== null && key !== undefined) {
            return this.dataCollector[key];
        }
        return undefined;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DataCollectorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DataCollectorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DataCollectorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

const TAGGING_CONSTANT = {
    "544cTopNavNavigationItemClicked": {
        "test_id": "544 c Top Nav - Navigation Item Clicked",
        "action_type": "event",
        "app_hierarchy": "enable|top nav|navigation item clicked",
        "event": "navigation_item_clicked"
    },
    "324ad0SearchPanelHowCanWeHelpShow": {
        "test_id": "324 ad0 Search Panel - How Can We Help - Show",
        "action_type": "event",
        "app_hierarchy": "public|search panel|how can we help|show"
    },
    "324ad1SearchPanelHowCanWeHelpHide": {
        "test_id": "324 ad1 Search Panel - How Can We Help - Hide",
        "action_type": "event",
        "app_hierarchy": "public|search panel|how can we help|show"
    },
    "61aCreditCardServicesLanding": {
        "action_type": "page view",
        "app_hierarchy": "services|credit card services|landing",
        "test_id": "61 a Credit Card Services Landing",
        "event": "navigation_item_displayed"
    },
    "61a2CreditCardServiceLanding2ndLevelNavigationLinkClicked": {
        "action_type": "event",
        "app_hierarchy": "services|credit card services|landing:2nd level navigation link clicked",
        "test_id": "61 a2 Credit Card Services Landing - 2nd Level Navigation Link Clicked",
        "event": "navigation_item_clicked"
    },
    "62aBankingServicesLanding": {
        "action_type": "page view",
        "app_hierarchy": "services|banking services|landing",
        "test_id": "62 a Banking Services Landing",
        "event": "navigation_item_displayed"
    },
    "62a2BankingServicesLanding2ndLevelNavigationLinkClicked": {
        "action_type": "event",
        "app_hierarchy": "services|banking services|landing:2nd level navigation link clicked",
        "test_id": "62 a2 Banking Services Landing - 2nd Level Navigation Link Clicked",
        "event": "navigation_item_clicked"
    },
    "543aFooterLinkClicked": {
        "action_type": "event",
        "test_id": "543 a Footer - Link Clicked",
        "event": "footer_link_click",
    },
    // Start Citi Mobile Modal Tagging
    /**** Modal Displayed *****/
    "381hDashboard2Modal": {
        "action_type": 'page view',
        "app_hierarchy": 'enable|custom smart banner|modal',
        "form_type": 'Get it Done with Citi Mobile|Over 15 million',
        "test_id": '648 b Enable - Custom Smart Banner - Modal'
    },
    /**** Modal Close Icon *****/
    "381h2Dashboard2ModalInteractionClick": {
        "action_type": 'event',
        "app_hierarchy": 'enable|custom smart banner|modal:cta clicked',
        "event": 'custom_link_click',
        "page_inter": 'Close',
        "form_type": 'Citi Mobile|Get it Done with Citi Mobile',
        "test_id": '648 b2 Enable - Custom Smart Banner - Modal - CTA Clicked'
    },
    /**** Modal Use The App Button *****/
    "381h3Dashboard2ModalCTAClick": {
        "action_type": 'event',
        "app_hierarchy": 'enable|custom smart banner|modal:cta clicked',
        "event": 'custom_link_click',
        "link_name": 'Use the App',
        "form_type": 'Citi Mobile|Get it Done with Citi Mobile',
        "test_id": '648 b2 Enable - Custom Smart Banner - Modal - CTA Clicked'
    },
    /**** Modal Not Now Button *****/
    "381h3Dashboard2ModalCTABtnlick": {
        "action_type": 'event',
        "app_hierarchy": 'enable|custom smart banner|modal:cta clicked',
        "event": 'custom_link_click',
        "link_name": 'Not Now',
        "form_type": 'Citi Mobile|Get it Done with Citi Mobile',
        "test_id": '648 b2 Enable - Custom Smart Banner - Modal - CTA Clicked'
    },
    // End Citi Mobile Modal Tagging
    // Start Smart Banner Tagging
    /**** Smart Banner Displayed *****/
    "648aEnableCustomSmartBannerDisplayed": {
        "action_type": 'event',
        "event": 'dynamic_content_view',
        "app_hierarchy": 'enable|custom smart banner|displayed',
        "form_type": 'Citi Mobile',
        "test_id": '648 a Enable - Custom Smart Banner - Displayed'
    },
    /**** Smart Banner Use CTA Clicked *****/
    "648a2EnableCustomSmartBannerCTAClicked": {
        "action_type": 'event',
        "event": 'custom_link_click',
        "app_hierarchy": 'enable|custom smart banner|cta clicked',
        "link_name": 'Go',
        "form_type": 'Citi Mobile',
        "test_id": '648 a2 Enable - Custom Smart Banner - CTA Clicked'
    },
    /**** Smart Banner Close Icon CTA Clicked *****/
    "648a2EnableCustomSmartBannerCTACloseIconClicked": {
        "action_type": 'event',
        "event": 'custom_link_click',
        "app_hierarchy": 'enable|custom smart banner|cta clicked',
        "link_name": 'Close',
        "form_type": 'Citi Mobile',
        "test_id": '648 a2 Enable - Custom Smart Banner - CTA Clicked'
    },
    // End Smart Banner Tagging Tagging
    /**** Global Error Handler *****/
    "546aEnableGlobalError": {
        "action_type": "event",
        "test_id": "546 a Enable - Global Error",
        "event": "custom_error",
        "app_hierarchy": 'public|enable|global|error'
    },
    // End Global Error Handler
    /**** Browser support and troubleshooting page Tagging *****/
    "655BrowserWarningPage": {
        "action_type": "page view",
        "test_id": "655 Browser Warning Page",
        "app_hierarchy": "software|browser warning|landing"
    },
    /**** Browser support and troubleshooting CTA click *****/
    "655BrowserWarningCTAClicks": {
        "action_type": "event",
        "test_id": "655 Browser Warning CTA Clicks",
        "event": "custom_link_click",
        "app_hierarchy": "software|browser warning|landing:cta click"
    },
    /**** Session timeout Modal *****/
    "654SessionTimeoutPage": {
        "action_type": "page view",
        "test_id": "654 Session Timeout Page",
        "event": "custom_error",
        "app_hierarchy": "signout|session timeout|landing"
    },
    /**** Session timeout Modal Close/Continue/End *****/
    "654_bSessionTimeoutCTAClick": {
        "action_type": "event",
        "test_id": "654_b  Session Timeout CTA Click",
        "event": "custom_link_click",
        "app_hierarchy": "signout|session timeout|landing:cta click"
    },
    /**** Flex Pay T&C page Tagging *****/
    "366_4aFlexPayPartnerTermsandConditionsLanding": {
        "action_type": "page view",
        "test_id": "366_4 a Flex Pay - Partner Terms and Conditions - Landing",
        "app_hierarchy": "citi flex pay|partner terms and conditions|landing"
    },
    /**** Error Page  Tagging *****/
    "656aGerenralErrorPageSecure": {
        "action_type": "page view",
        "test_id": "656 a Gerenral Error Page Secure",
        "app_hierarchy": "account|general error|landing"
    },
    /**** Error Page  Tagging *****/
    "656bGerenralErrorPageSecureCTAClicks": {
        "action_type": "event",
        "test_id": "656 b Gerenral Error Page Secure CTA Clicks"
    },
    /**** Error Page  Tagging *****/
    "656cGerenralErrorPagePublic": {
        "action_type": "page view",
        "test_id": "656 c Gerenral Error Page Public",
        "app_hierarchy": "account|general error|landing"
    },
    /**** Error Page  Tagging *****/
    "656dGerenralErrorPagePublicCTAClicks": {
        "action_type": "event",
        "test_id": "656 d Gerenral Error Page Public CTA Clicks"
    }
};

class TaggingService {
    triggerPageTagging(nonIcmsId, isPostLogin, staticParam, options) {
        try {
            if (window) {
                let taggingDataLayer = window.taggingDataLayer;
                if (taggingDataLayer) {
                    let app_hierarchy_prefix = isPostLogin ? "secure|" : "public|";
                    let app_hierarchy = app_hierarchy_prefix + TAGGING_CONSTANT[staticParam]["app_hierarchy"];
                    let topNavObject = {
                        app_hierarchy: app_hierarchy
                    };
                    if (options?.dynamicContent.navigation_item_click_attribution) {
                        options.dynamicContent.navigation_item_click_attribution = app_hierarchy + '~' + options.dynamicContent.navigation_items;
                    }
                    const contentParam = options && options.dynamicContent ?
                        { ...TAGGING_CONSTANT[staticParam], ...options.dynamicContent, ...topNavObject } : { ...TAGGING_CONSTANT[staticParam], ...topNavObject };
                    staticParam ? taggingDataLayer.applicationData(nonIcmsId, contentParam) : taggingDataLayer.applicationData(TAGGING_CONSTANT[nonIcmsId]);
                }
            }
        }
        catch (e) {
            console.log('Error in Tagging');
        }
    }
    triggerEventTagging(nonIcmsId, staticParam, options) {
        try {
            if (window) {
                let taggingDataLayer = window.taggingDataLayer;
                if (taggingDataLayer) {
                    const contentParam = options && options.dynamicContent ?
                        { ...TAGGING_CONSTANT[staticParam], ...options.dynamicContent } : { ...TAGGING_CONSTANT[staticParam] };
                    staticParam ? taggingDataLayer.updateEventData(nonIcmsId, contentParam) : taggingDataLayer.updateEventData(TAGGING_CONSTANT[nonIcmsId]);
                }
            }
        }
        catch (e) {
            console.log('Error in Tagging');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: TaggingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: TaggingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: TaggingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

// MessagingService
class MessagingService {
    constructor(_appConfigService) {
        this._appConfigService = _appConfigService;
        this.messageEvent = {};
        this.getMessage = new BehaviorSubject('');
    }
    /**
     * The sendMessage method is called for sending and receiving data.
     * method: sendMessage
     */
    sendMessage(eventName, type, source) {
        let config = this._appConfigService.getMessageConfig();
        let origin = config ? config['messageOrigin'] : source;
        let appId = config ? config['messageAppId'] : '';
        this.messageEvent.eventName = eventName + '_Init';
        this.messageEvent.appId = appId;
        //receive message listener
        window.addEventListener('message', event => {
            if (typeof event.data === 'string' && event.data.includes(eventName)) {
                const parsedData = JSON.parse(event.data);
                if (parsedData.eventName === eventName + '_Response' && event.origin === origin) {
                    console.log('## CONTAINER MESSAGING = ', event);
                    //emitting the message
                    this.getMessage.next(event.data);
                }
            }
        });
        //postMessage
        let windowFrameStyle = (type == 'iframe') ? window.top : (type == 'opener') ? window.opener : (type == 'parent') ? window.parent : window.top;
        windowFrameStyle.postMessage(JSON.stringify(this.messageEvent), origin);
    }
    /**
     * The receiveMessage method is called alone for receiving data.
     * method: receiveMessage
     */
    receiveMessage(data, origin) {
        window.addEventListener('message', event => {
            if ((typeof event.data === 'string' && event.data.includes(data)) || event.origin === origin) {
                const parsedData = JSON.parse(event.data);
                console.log('## CONTAINER MESSAGING = ', parsedData);
                //emitting the message
                this.getMessage.next(parsedData);
            }
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: MessagingService, deps: [{ token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: MessagingService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: MessagingService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AppConfigService }] });

// GlobalErrorhandler
class GlobalErrorHandlerService {
    constructor(injector, _taggingService, _appConfigService, _router) {
        this.injector = injector;
        this._taggingService = _taggingService;
        this._appConfigService = _appConfigService;
        this._router = _router;
        _router.events
            .pipe(filter(event => event instanceof NavigationStart))
            .subscribe(event => {
            console.log('Accessed Route', event['url']);
            this.accessedRoute = event['url'];
        });
    }
    /**
     * The handleError method is used for capturing different errors globally and perform set of actions as needed.
     * method: handleError
     */
    handleError(error) {
        console.error(error);
        if (error && error.message && error.message.includes('Loading chunk')) {
            // Capture the specific chunk failed to load (if available)
            const regex = /error:\s*([^\s)]+)/;
            const chunkFailedMessage = error.message.match(regex);
            const chunkFailed = chunkFailedMessage
                ? chunkFailedMessage[1]
                : error.message;
            // Determine the type of error (network, missing file, etc.)
            let errorType = 'ChunkLoadError';
            if (error.message.includes('missing')) {
                errorType = 'Missing file';
            }
            else if (error.name === 'NetworkError') {
                errorType = 'Network error';
            }
            const titleString = `${errorType}, Chunk: ${chunkFailed}`;
            this.errorHandlerTagging(titleString);
            window.location.href = this._appConfigService.getcsrHostName() + '/US/ag' + this.accessedRoute;
        }
    }
    /**
     * @Service: Global Error Handler Service
     * @Section: error handler tagging
     * @Description: This method is used for calling tagging service on chunk load error as per specifications
     */
    errorHandlerTagging(custError) {
        this._taggingService.triggerEventTagging('nonicms_content', '546aEnableGlobalError', {
            dynamicContent: {
                cust_error: custError
            }
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: GlobalErrorHandlerService, deps: [{ token: i0.Injector }, { token: TaggingService }, { token: AppConfigService }, { token: i3$1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: GlobalErrorHandlerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: GlobalErrorHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.Injector }, { type: TaggingService }, { type: AppConfigService }, { type: i3$1.Router }] });

// PostSignoffService
class PostSignoffService {
    constructor(_window, _appConfigService) {
        this._window = _window;
        this._appConfigService = _appConfigService;
    }
    clearCookiesAndSessionStorage() {
        this.postNgaCoExist = Cookie.get('NGACoExistenceCookie');
        if (this.postNgaCoExist) {
            this.isNgaLogin = false;
        }
        else {
            this.isNgaLogin = true;
        }
        sessionStorage.setItem('isNgaLogin', JSON.stringify(this.isNgaLogin));
        const cookiesToDelete = ['SessionRecoveryCookie', 'isSecondaryLogin', 'DVAModePersist', 'zeroPredictionToggle', 'NGACoExistenceCookie', 'NGAKeepAOAlive', 'client_id', 'apimBaseUrl', 'appVersion', 'businessCode', 'channelId', 'countryCode', 'tenantId', 'bcsid', 'qrCodeLogin', 'ngaDeepdrop', 'interstitialSeen', 'ts', 'citi_authorization', 'cbol_timeout', 'timeout_modal_state'];
        const sessionStorageToDelete = [
            'NgaKATimeInterVal', 'wealthDropDownSelection', 'sessionPostBrandingStorage', 'sessionJFP_TOKEN', 'sessionJFP_ID', 'siteCatUserProp', 'helpCenterFlags', 'searchPanelOpen', 'wealthWhatsNextTileDismissed',
            'partnerId', 'deepDropDLP', 'OFFER_interstitial', 'KYC_SUBMISSION_STATUS', 'KYC_interstitial', 'SETTLE_AGENT', 'firstDisplay', 'suspectedCards', 'currentCardIndex', 'selectedTrans', 'completedDetectedCardIndex', 'visitedOffer', 'nextBestActions', 'popupVisited', 'chatData', 'iraData', 'iraDefaultContent', 'mopData', 'alreadyRedirected', 'citi_authorization', 'searchPanelSettings', 'hideConsolidationTile',
            'quickactionprefillvalue', 'setAuthCode', 'isTierAccelerationEnrolled', 'postInterstitialTargetUri', 'partnerLogin', 'cccSSOUniqueID', 'cccHRTSet', 'cccUpcomingMeet', 'conversionOverlayShown', 'nextRoute', 'smartBanner', 'rmfaBio'
        ];
        for (const cookieKey of cookiesToDelete) {
            console.log(Cookie.get(cookieKey));
            Cookie.delete(cookieKey, '/');
            Cookie.delete(cookieKey, '/', '.citi.com');
        }
        for (const sessionStorageKey of sessionStorageToDelete) {
            console.log(sessionStorage.getItem(sessionStorageKey));
            sessionStorage.removeItem(sessionStorageKey);
            console.log(sessionStorage.getItem(sessionStorageKey));
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: PostSignoffService, deps: [{ token: WINDOW }, { token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: PostSignoffService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: PostSignoffService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: AppConfigService }] });

class ModalSharedService {
    constructor() {
        this.dialogStateSource = new Subject();
        this.dialogState$ = this.dialogStateSource.asObservable();
    }
    openDialog() {
        this.dialogStateSource.next(true);
    }
    closeDialog() {
        this.dialogStateSource.next(false);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: ModalSharedService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: ModalSharedService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: ModalSharedService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class QaCitiMobileModal {
    constructor() {
        // #region // ---------- Properties ---------- //
        /**
         * @property showCitiMobile
         * @description Whether cds Modal open or not
         */
        this.showCitiMobile = false;
        /**
         * @property useAppNavigate
         * @description emits when the use app button is clicked
         * @twoway false.
         */
        this.useAppNavigate = new EventEmitter();
        /**
         * @property notNowCloseModal
         * @description emits when the launchQR Code Mobile button is clicked
         * @twoway false.
         */
        this.notNowCloseModal = new EventEmitter();
        /**
         * @property emitTaggingForQRcodeCTA
         * @description emits when the QRcodeCTA button is clicked
         * @twoway false.
         */
        this.emitTaggingForQRcodeCTA = new EventEmitter();
        /**
       * @property closeCitiModalIcon
       * @description emits when the closeCitiModal Icon is clicked
       * @twoway false.
       */
        this.closeCitiModalIcon = new EventEmitter();
    }
    // #endregion
    // #region // ------------ Methods ------------ //
    /**
     * @Method: onUseAppClick
     * @description Opens new tab for provided link.
     * @return Void
     */
    onUseAppClick(url, qaObjCopy) {
        this.useAppNavigate.emit();
        this.triggerTagging(qaObjCopy);
    }
    /**
     * @Method: onCloseIcon
     * @description Close Citi Modal.
     * @return Void
     */
    onCloseIcon(qaObjCopy) {
        this.showCitiMobile = false;
        this.closeCitiModalIcon.emit();
        this.triggerTagging(qaObjCopy);
    }
    /**
     * @Method: onNotNowBtn
     * @description Opens/close new QrCode Modal.
     * @return Void
     */
    onNotNowBtn(qaObjCopy) {
        this.notNowCloseModal.emit();
        this.triggerTagging(qaObjCopy);
    }
    triggerTagging(qaObjCopy) {
        this.emitTaggingForQRcodeCTA.emit(qaObjCopy);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: QaCitiMobileModal, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: QaCitiMobileModal, selector: "qa-citi-mobile-modal", inputs: { showCitiMobile: "showCitiMobile", quickHref: "quickHref", qaCitiMobileContent: "qaCitiMobileContent", selectedView: "selectedView", productName: "productName" }, outputs: { useAppNavigate: "useAppNavigate", notNowCloseModal: "notNowCloseModal", emitTaggingForQRcodeCTA: "emitTaggingForQRcodeCTA", closeCitiModalIcon: "closeCitiModalIcon" }, ngImport: i0, template: "<cds-modal-dialog [(isOpen)]=\"showCitiMobile\" [isClosedOnBackdropClick]=\"false\">\n  <div class=\"cds-modal-dialog-centered\">\n    <div\n      class=\"cds-modal2-content\"\n      cdsModalInitialFocus\n      role=\"dialog\"\n      tabindex=\"0\"\n    >\n      <div class=\"close-button-wrap\">\n        <button\n          class=\"cds-modal2-close icon-btn\"\n          cdsButton=\"secondary\"\n          buttonIcon=\"icon\"\n          size=\"small\"\n          type=\"button\"\n          aria-label=\"close\"\n          (click)=\"onCloseIcon()\"\n        >\n          <!-- replace cds-icon due to style issue -->\n          <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\">\n            <path\n              fill-rule=\"evenodd\"\n              clip-rule=\"evenodd\"\n              d=\"m12.787 12 9.05-9.05a.557.557 0 0 0-.787-.787L12 11.213l-9.05-9.05a.557.557 0 0 0-.787.787l9.05 9.05-9.05 9.05a.557.557 0 0 0 .787.787l9.05-9.05 9.05 9.05a.557.557 0 0 0 .787-.787L12.787 12Z\"\n              fill=\"#056DAE\"\n            ></path>\n          </svg>\n        </button>\n      </div>\n      <div class=\"mobile-image\">\n        <img\n          src=\"/CBOL/IA/Angular/assets/mobileResponsiveCitiModal.svg\"\n          alt=\"mobile image\"\n        />\n      </div>\n      <div class=\"cds-modal2-header top-margin padding-left-right\">\n        <div class=\"cds-modal-title\" role=\"heading\" aria-level=\"1\">\n          {{qaCitiMobileContent?.quickTitle}}\n        </div>\n      </div>\n      <div class=\"cds-modal2-body padding-left-right\">\n        <p class=\"first-p\">\n          <span class=\"sub-title-first\"\n            >{{qaCitiMobileContent?.quickContent1Subheader}}</span\n          >\n          {{qaCitiMobileContent?.quickContent1}}\n        </p>\n\n        <span class=\"info-content\"\n          ><span class=\"sub-title-second\"\n            >{{qaCitiMobileContent?.quickContent2Subheader}}</span\n          >\n          <p>\n            If you tap \u201CUse the App\u201D you\u2019ll be leaving\n            <a target=\"_blank\" class=\"qa-link\" href=\"https://www.citi.com\"\n              >Citi.com</a\n            >. If you haven\u2019t downloaded Citi Mobile yet, you\u2019ll be taken to a\n            third-party site to download it. That site may have a different\n            privacy and security policy and may provide you less privacy and\n            security than Citi. Citi and its affiliates are not responsible for\n            the products, services and content on the third-party site.\n          </p></span\n        >\n      </div>\n      <div class=\"cds-modal2-footer padding-left-right top-padding\">\n        <button\n          role=\"link\"\n          class=\"ml-left\"\n          cdsButton=\"primary\"\n          (click)=\"onUseAppClick(quickHref, qaCitiMobileContent.quickPrimaryBtn)\"\n        >\n          {{qaCitiMobileContent?.quickPrimaryBtn}}\n        </button>\n\n        <button\n          cdsButton=\"secondary\"\n          (click)=\"onNotNowBtn(qaCitiMobileContent?.quickSecondryBtn)\"\n        >\n          {{qaCitiMobileContent?.quickSecondryBtn}}\n        </button>\n      </div>\n    </div>\n  </div>\n</cds-modal-dialog>\n", styles: [".cds-modal-fade{transition:opacity .15s linear}body.cds-modal-open{overflow:hidden}body.cds-modal-open .cds-modal{overflow-x:hidden;overflow-y:auto}.cds-modal-backdrop{position:fixed;top:0;left:0;background-color:#000;width:100vw;height:100vh;z-index:10000;opacity:.6}.cds-modal-backdrop.cds-modal-fade{opacity:0}.cds-modal-backdrop.cds-modal-show{opacity:.6}.cds-modal{position:fixed;top:0;left:0;z-index:10001;width:100%;height:100%;overflow:hidden;display:none}.cds-modal .cds-modal-dialog{transform:translateY(-2rem);opacity:0}.cds-modal.cds-modal-fade .cds-modal-dialog{transition:transform .3s ease-out,opacity .3s linear;transform:translateY(-2rem);opacity:0}.cds-modal.cds-modal-show .cds-modal-dialog{transform:none;opacity:1}.cds-modal-dialog{display:flow-root;pointer-events:none;height:100%}.cds-modal2-content{display:flex;flex-direction:column;width:calc(100% - 1rem);background-color:#fff;border-radius:8px;pointer-events:auto;margin:.5rem;position:relative}.cds-modal2-content h4{font-weight:700;font-size:36px;margin-bottom:0}.cds-modal2-content .cds-button-secondary,.cds-modal2-content .cds-button-tertiary{margin-left:16px}@media (min-width: 576px){.cds-modal2-content{margin:1.5rem auto;max-width:684px}}@media (min-width: 576px){.cds-modal2-sm{max-width:448px}}.cds-modal2-sm h4{font-size:28px}.cds-modal2-sm h5{font-size:21px}@media (min-width: 992px){.cds-modal2-lg,.cds-modal2-xl{max-width:856px}}@media (min-width: 1200px){.cds-modal2-xl{max-width:1156px}}.cds-modal2-header,.cds-modal2-body,.cds-modal2-footer{padding-left:40px;padding-right:40px}.cds-modal2-close{display:flex;position:absolute;right:0;padding:0;margin:12px 12px 0}.cds-modal2-header{display:flex;margin-top:48px;padding-bottom:24px;flex-direction:column}.cds-modal2-header .cds-modal2-close{margin:-1rem -1rem -1rem auto}.cds-modal2-content .centered{text-align:center;align-content:center;justify-content:center}.cds-modal2-content .steps h6{display:flex;margin-top:40px;padding-left:40px;padding-right:40px;font-size:16px;margin-bottom:8px}.cds-modal2-content .steps .cds-modal2-header{margin-top:0}.cds-modal2-content .steps h6.centered{padding-left:40px}.cds-modal2-content .icons{display:flex;flex-direction:column;margin-top:40px}.cds-modal2-content .icons cds-icon{padding-left:40px;padding-bottom:1em}.cds-modal2-content .icons .cds-modal2-header{margin-top:0}.cds-modal2-content .icons.centered cds-icon{padding-left:0}.cds-modal2-content .secondary-button{margin-left:10px}.cds-modal2-sm .secondary-button{margin-right:0;margin-top:16px}.cds-modal2-sm .cds-button-tertiary{margin-top:16px;margin-left:0}.cds-modal2-content .subtitle{margin-bottom:0}.cds-modal2-content h5{margin-top:10px;font-size:28px;margin-bottom:0}.cds-modal2-content .imgs-left{display:flex;flex-direction:row}.cds-modal2-content .imgs-left .cds-modal2-header,.cds-modal2-content .imgs-left h5,.cds-modal2-content .imgs-left .cds-modal2-body,.cds-modal2-content .imgs-left .cds-modal2-footer{padding-left:40px;padding-right:40px}.cds-modal2-content .imgs-left img{padding-left:40px;padding-top:70px;padding-bottom:70px;object-fit:cover;align-self:center}.cds-modal2-content .imgs-right{display:flex;flex-direction:row}.cds-modal2-content .imgs-right .cds-modal2-header,.cds-modal2-content .imgs-right h5,.cds-modal2-content .imgs-right .cds-modal2-body,.cds-modal2-content .imgs-right .cds-modal2-footer{padding-right:40px}.cds-modal2-content .imgs-right img{padding-right:40px;padding-top:70px;padding-bottom:70px;object-fit:cover;align-self:center}.cds-modal2-content .secondary-buttons{display:flex;flex-direction:column}.cds-modal2-content .secondary-buttons .button-wrap-1{padding-left:40px;padding-bottom:1em;padding-right:2em}.cds-modal2-content .secondary-buttons .button-wrap-2{margin-bottom:40px;padding-left:40px}.cds-modal2-content .secondary-buttons .button-wrap-2 button{margin-left:3px;border:none}.cds-modal2-content .secondary-buttons button.image-text cds-icon{padding-right:5px}.cds-modal2-content .secondary-buttons.centered .button-wrap-1{padding-left:40px}.cds-modal2-content .secondary-buttons.centered .button-wrap-2{padding-left:0}.cds-modal2-footer.secondary-buttons.centered{padding-bottom:0}@media only screen and (min-width: 990px){.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text){flex-direction:row}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{display:flex;flex:1 1 auto;padding-bottom:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{flex:1 1 auto;display:flex;padding-right:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .button-wrap-1,.cds-modal2-xl .button-wrap-1{padding-left:0!important;padding-right:0;padding-bottom:0!important}.cds-modal2-lg .button-wrap-2,.cds-modal2-xl .button-wrap-2{padding-bottom:0;padding-top:3px;padding-right:0!important;margin-bottom:0!important}.cds-modal2-lg .secondary-buttons,.cds-modal2-xl .secondary-buttons{flex-direction:row}.cds-modal2-lg .secondary-buttons .button-wrap-1,.cds-modal2-xl .secondary-buttons .button-wrap-1{display:flex;flex:1 1 auto}.cds-modal2-lg .secondary-buttons .button-wrap-2,.cds-modal2-xl .secondary-buttons .button-wrap-2{display:flex;flex:1 1 auto;margin-bottom:0}.cds-modal2-lg .secondary-buttons .button-wrap-2 .first,.cds-modal2-xl .secondary-buttons .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .secondary-buttons.centered,.cds-modal2-xl .secondary-buttons.centered{padding-bottom:40px!important}.cds-modal2-lg .secondary-buttons.centered .button-wrap-1,.cds-modal2-xl .secondary-buttons.centered .button-wrap-1{padding-left:0}}.cds-modal2-sm .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1{display:flex;flex-direction:row;padding-right:40px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .secondary-button,.cds-modal2-sm .secondary-buttons .button-wrap-1 .cds-button-tertiary{margin-left:16px}.cds-modal2-sm .secondary-buttons .button-wrap-1 button{margin-top:0}.cds-modal2-sm .secondary-buttons .button-wrap-2{padding-left:0;margin-left:40px}.cds-modal2-sm .secondary-buttons.centered .button-wrap-1 button{flex:1 1 auto}.cds-modal2-sm .secondary-buttons.centered .button-wrap-2{margin-left:0}.cds-modal2-sm .cds-modal2-footer:not(.vertical-buttons) .cds-button-tertiary{margin-top:0;margin-left:16px}.cds-modal2-sm .cds-modal2-footer.vertical-buttons{display:flex;flex-direction:column}.cds-modal2-sm .cds-modal2-footer.vertical-buttons .secondary-button{margin-left:0}.cds-modal2-sm .cds-modal2-footer.vertical-buttons button{align-self:center;width:100%}.cds-modal2-content .display-top{display:none}.cds-modal2-content .display-bottom{display:block}.cds-modal2-dialog-scrollable{display:flex;flex-direction:column}.cds-modal2-dialog-scrollable .cds-modal2-body{overflow-y:auto;max-height:40vh}.cds-modal2-dialog-scrollable .cds-modal2-footer,.cds-modal2-dialog-scrollable .secondary-buttons{padding-top:40px}@media only screen and (min-width: 645px) and (max-width: 990px){.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text){flex-direction:row}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{display:flex;flex:1 1 auto;padding-bottom:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{flex:1 1 auto;display:flex;padding-right:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1,.cds-modal2-xl .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{padding-bottom:0!important;padding-left:0}.cds-modal2-lg .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2,.cds-modal2-xl .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{padding-right:0!important;margin-bottom:0}.cds-modal2-lg .secondary-buttons.triple-button-secondary-text .button-wrap-1,.cds-modal2-xl .secondary-buttons.triple-button-secondary-text .button-wrap-1{padding-left:0}.cds-modal2-lg .secondary-buttons.triple-button-secondary-text .button-wrap-2,.cds-modal2-xl .secondary-buttons.triple-button-secondary-text .button-wrap-2{margin-bottom:0;padding-left:0}.cds-modal2-dialog-scrollable .cds-modal2-body{max-height:25vh}.cds-modal2-dialog-scrollable:has(.cds-modal-border-top) .cds-modal2-body{max-height:10vh}}.cds-modal2-content .imgs-left h5,.cds-modal2-content .imgs-right h5{padding-left:0}@media only screen and (max-width: 645px){.cds-modal2-content .cds-button-tertiary{margin-top:16px;margin-left:0}.cds-modal2-content .cds-button-secondary{margin-right:0;margin-top:16px;margin-left:0}.cds-modal2-dialog-scrollable .cds-modal2-body{height:20vh}.cds-modal2-content .display-top{display:block}.cds-modal2-content .display-bottom{display:none}.cds-modal2-content .imgs-left,.cds-modal2-content .imgs-right{flex-direction:column}.cds-modal2-content .imgs-left img,.cds-modal2-content .imgs-right img{padding-right:0;padding-bottom:0;padding-left:0}.cds-modal2-content .secondary-buttons.centered .button-wrap-2{display:flex;flex-direction:row;margin-bottom:40px}.cds-modal2-sm .cds-button-tertiary{margin-top:16px!important;margin-left:0!important}.cds-modal2-sm .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1{display:flex;flex-direction:column;padding-right:40px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .cds-button-tertiary{margin-top:16px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .secondary-button{margin-top:16px;margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1 button{align-self:center;width:100%}.cds-modal2-sm .secondary-buttons .button-wrap-2{align-self:center;padding-left:0;margin-left:0}.cds-modal2-dialog-scrollable .cds-modal2-sm .cds-button-tertiary{margin-top:16px!important;margin-left:0!important}.cds-modal2-lg .button-wrap-1,.cds-modal2-xl .button-wrap-1{padding-right:0!important;padding-left:0!important}.cds-modal2-lg .button-wrap-2,.cds-modal2-xl .button-wrap-2{display:flex;flex-direction:row;margin-bottom:0}.cds-modal2-content .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-content .secondary-buttons .button-wrap-1{display:flex;flex-direction:column;padding-right:40px}.cds-modal2-content .secondary-buttons .button-wrap-1 button{align-self:center;width:100%}.cds-modal2-content .secondary-buttons .button-wrap-2{align-self:center;padding-left:0}.cds-modal2-content .secondary-buttons .secondary-text{margin-left:0}.cds-modal2-footer{display:flex;flex-direction:column}.cds-modal2-footer .secondary-button{margin-left:0}.cds-modal2-footer button{align-self:center;width:100%}}.cds-modal2-content .no-button{margin-bottom:40px;padding-bottom:0}.cds-modal2-content .close-btn{outline:none;border:none;background-color:transparent}.cds-modal2-content .close-btn:focus{outline:auto}.cds-modal2-body{font-size:15px;padding-left:40px}.cds-modal2-body p,.cds-modal-title{margin-bottom:0}.cds-modal2-footer{display:flex;padding-top:40px;padding-bottom:40px}.cds-modal2-content .secondary-buttons{padding-top:40px}.cds-modal2-content .cds-modal-border-top{border-top:1px solid #cccccc;padding-top:40px;margin-top:20px}.cds-modal2-content .cds-modal-border-bottom{border-bottom:1px solid rgba(0,0,0,.2509803922)}.cds-modal-dialog-centered{display:flex;align-items:center;min-height:100%}.cds-modal-dialog-centered.cds-modal2-dialog-scrollable{justify-content:center}body.cds-modal-open citi-header div.primary-header{z-index:0!important}.cds-button{outline:none}.cds-button-inlineLink{font-family:Interstate_Light,sans-serif;text-decoration:underline;border-radius:6px;border-color:transparent;height:auto;margin:auto 0}.cds-button-inlineLink:focus{box-shadow:0 0 0 1px #002a54 inset}.cds-button-inlineLink:hover,.cds-button-inlineLink:active{text-decoration:underline}.cds-button-inlineLink:disabled{background-color:transparent;border-color:transparent}.cds-button-inlineLink.cds-button-icon,.cds-button-inlineLink.cds-button-icon-round{padding:0!important}.cds-button-inlineLink.cds-button-chevron-right,.cds-button-inlineLink.cds-button-chevron-left{text-decoration:none}.cds-button-tertiary{text-decoration:underline;border-color:transparent}.cds-button-tertiary:hover,.cds-button-tertiary:active{text-decoration:underline}.cds-button-tertiary.cds-button-chevron-right,.cds-button-tertiary.cds-button-chevron-left{text-decoration:none}.cds-button-secondaryInverse cds-icon path{fill:#fff!important}.cds-button-secondaryInverse:focus{box-shadow:0 0 0 1px #002a54,0 0 0 2px #056dae}.cds-modal-title{font-family:Interstate_Regular,sans-serif;font-weight:500;font-size:24px;line-height:32px}.cds-modal-title .copy-right{position:relative;bottom:4;right:8px}.sub-title-first{font-family:Interstate;font-size:16px;font-weight:700;line-height:24px;letter-spacing:0px}.first-p{font-size:16px;line-height:24px}.sub-title-second{font-family:Interstate;font-size:12px;font-weight:700;line-height:18px;letter-spacing:0px;margin-bottom:8px;display:block}.info-content{margin-top:24px;display:block}.mobile-image{width:100%;height:100%;margin-top:5px}.mobile-image img{width:100%;height:100%;padding:0 24px}.qa-link{color:#056dae;text-decoration-line:underline}.close-button-wrap{display:flex;align-items:center;margin-top:16px;height:48px}.close-button-wrap .icon-btn{border:none;margin:0 17px 0 0;padding:16px}.close-button-wrap .icon-btn svg{width:24px;height:24px}.top-margin{margin-top:24px}.padding-left-right{padding-right:24px;padding-left:24px}.cds-modal2-body{text-align:justify}.top-padding{padding-top:24px}\n"], dependencies: [{ kind: "component", type: i1.CdsModalDialogComponent, selector: "cds-modal-dialog", inputs: ["isOpen", "isClosedOnBackdropClick", "isClosedOnEscape", "hasFade", "ariaRole", "isRestoreFocusDisabled", "ariaLabel", "ariaLabelledby", "fadeTime"], outputs: ["isOpenChange", "openStart", "openEnd", "closeStart", "closeEnd"] }, { kind: "directive", type: i1.CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: ["cdsButton", "size", "buttonIcon", "arrowRight", "arrowLeft", "JFPToken"], exportAs: ["cdsButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: QaCitiMobileModal, decorators: [{
            type: Component,
            args: [{ selector: 'qa-citi-mobile-modal', template: "<cds-modal-dialog [(isOpen)]=\"showCitiMobile\" [isClosedOnBackdropClick]=\"false\">\n  <div class=\"cds-modal-dialog-centered\">\n    <div\n      class=\"cds-modal2-content\"\n      cdsModalInitialFocus\n      role=\"dialog\"\n      tabindex=\"0\"\n    >\n      <div class=\"close-button-wrap\">\n        <button\n          class=\"cds-modal2-close icon-btn\"\n          cdsButton=\"secondary\"\n          buttonIcon=\"icon\"\n          size=\"small\"\n          type=\"button\"\n          aria-label=\"close\"\n          (click)=\"onCloseIcon()\"\n        >\n          <!-- replace cds-icon due to style issue -->\n          <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\">\n            <path\n              fill-rule=\"evenodd\"\n              clip-rule=\"evenodd\"\n              d=\"m12.787 12 9.05-9.05a.557.557 0 0 0-.787-.787L12 11.213l-9.05-9.05a.557.557 0 0 0-.787.787l9.05 9.05-9.05 9.05a.557.557 0 0 0 .787.787l9.05-9.05 9.05 9.05a.557.557 0 0 0 .787-.787L12.787 12Z\"\n              fill=\"#056DAE\"\n            ></path>\n          </svg>\n        </button>\n      </div>\n      <div class=\"mobile-image\">\n        <img\n          src=\"/CBOL/IA/Angular/assets/mobileResponsiveCitiModal.svg\"\n          alt=\"mobile image\"\n        />\n      </div>\n      <div class=\"cds-modal2-header top-margin padding-left-right\">\n        <div class=\"cds-modal-title\" role=\"heading\" aria-level=\"1\">\n          {{qaCitiMobileContent?.quickTitle}}\n        </div>\n      </div>\n      <div class=\"cds-modal2-body padding-left-right\">\n        <p class=\"first-p\">\n          <span class=\"sub-title-first\"\n            >{{qaCitiMobileContent?.quickContent1Subheader}}</span\n          >\n          {{qaCitiMobileContent?.quickContent1}}\n        </p>\n\n        <span class=\"info-content\"\n          ><span class=\"sub-title-second\"\n            >{{qaCitiMobileContent?.quickContent2Subheader}}</span\n          >\n          <p>\n            If you tap \u201CUse the App\u201D you\u2019ll be leaving\n            <a target=\"_blank\" class=\"qa-link\" href=\"https://www.citi.com\"\n              >Citi.com</a\n            >. If you haven\u2019t downloaded Citi Mobile yet, you\u2019ll be taken to a\n            third-party site to download it. That site may have a different\n            privacy and security policy and may provide you less privacy and\n            security than Citi. Citi and its affiliates are not responsible for\n            the products, services and content on the third-party site.\n          </p></span\n        >\n      </div>\n      <div class=\"cds-modal2-footer padding-left-right top-padding\">\n        <button\n          role=\"link\"\n          class=\"ml-left\"\n          cdsButton=\"primary\"\n          (click)=\"onUseAppClick(quickHref, qaCitiMobileContent.quickPrimaryBtn)\"\n        >\n          {{qaCitiMobileContent?.quickPrimaryBtn}}\n        </button>\n\n        <button\n          cdsButton=\"secondary\"\n          (click)=\"onNotNowBtn(qaCitiMobileContent?.quickSecondryBtn)\"\n        >\n          {{qaCitiMobileContent?.quickSecondryBtn}}\n        </button>\n      </div>\n    </div>\n  </div>\n</cds-modal-dialog>\n", styles: [".cds-modal-fade{transition:opacity .15s linear}body.cds-modal-open{overflow:hidden}body.cds-modal-open .cds-modal{overflow-x:hidden;overflow-y:auto}.cds-modal-backdrop{position:fixed;top:0;left:0;background-color:#000;width:100vw;height:100vh;z-index:10000;opacity:.6}.cds-modal-backdrop.cds-modal-fade{opacity:0}.cds-modal-backdrop.cds-modal-show{opacity:.6}.cds-modal{position:fixed;top:0;left:0;z-index:10001;width:100%;height:100%;overflow:hidden;display:none}.cds-modal .cds-modal-dialog{transform:translateY(-2rem);opacity:0}.cds-modal.cds-modal-fade .cds-modal-dialog{transition:transform .3s ease-out,opacity .3s linear;transform:translateY(-2rem);opacity:0}.cds-modal.cds-modal-show .cds-modal-dialog{transform:none;opacity:1}.cds-modal-dialog{display:flow-root;pointer-events:none;height:100%}.cds-modal2-content{display:flex;flex-direction:column;width:calc(100% - 1rem);background-color:#fff;border-radius:8px;pointer-events:auto;margin:.5rem;position:relative}.cds-modal2-content h4{font-weight:700;font-size:36px;margin-bottom:0}.cds-modal2-content .cds-button-secondary,.cds-modal2-content .cds-button-tertiary{margin-left:16px}@media (min-width: 576px){.cds-modal2-content{margin:1.5rem auto;max-width:684px}}@media (min-width: 576px){.cds-modal2-sm{max-width:448px}}.cds-modal2-sm h4{font-size:28px}.cds-modal2-sm h5{font-size:21px}@media (min-width: 992px){.cds-modal2-lg,.cds-modal2-xl{max-width:856px}}@media (min-width: 1200px){.cds-modal2-xl{max-width:1156px}}.cds-modal2-header,.cds-modal2-body,.cds-modal2-footer{padding-left:40px;padding-right:40px}.cds-modal2-close{display:flex;position:absolute;right:0;padding:0;margin:12px 12px 0}.cds-modal2-header{display:flex;margin-top:48px;padding-bottom:24px;flex-direction:column}.cds-modal2-header .cds-modal2-close{margin:-1rem -1rem -1rem auto}.cds-modal2-content .centered{text-align:center;align-content:center;justify-content:center}.cds-modal2-content .steps h6{display:flex;margin-top:40px;padding-left:40px;padding-right:40px;font-size:16px;margin-bottom:8px}.cds-modal2-content .steps .cds-modal2-header{margin-top:0}.cds-modal2-content .steps h6.centered{padding-left:40px}.cds-modal2-content .icons{display:flex;flex-direction:column;margin-top:40px}.cds-modal2-content .icons cds-icon{padding-left:40px;padding-bottom:1em}.cds-modal2-content .icons .cds-modal2-header{margin-top:0}.cds-modal2-content .icons.centered cds-icon{padding-left:0}.cds-modal2-content .secondary-button{margin-left:10px}.cds-modal2-sm .secondary-button{margin-right:0;margin-top:16px}.cds-modal2-sm .cds-button-tertiary{margin-top:16px;margin-left:0}.cds-modal2-content .subtitle{margin-bottom:0}.cds-modal2-content h5{margin-top:10px;font-size:28px;margin-bottom:0}.cds-modal2-content .imgs-left{display:flex;flex-direction:row}.cds-modal2-content .imgs-left .cds-modal2-header,.cds-modal2-content .imgs-left h5,.cds-modal2-content .imgs-left .cds-modal2-body,.cds-modal2-content .imgs-left .cds-modal2-footer{padding-left:40px;padding-right:40px}.cds-modal2-content .imgs-left img{padding-left:40px;padding-top:70px;padding-bottom:70px;object-fit:cover;align-self:center}.cds-modal2-content .imgs-right{display:flex;flex-direction:row}.cds-modal2-content .imgs-right .cds-modal2-header,.cds-modal2-content .imgs-right h5,.cds-modal2-content .imgs-right .cds-modal2-body,.cds-modal2-content .imgs-right .cds-modal2-footer{padding-right:40px}.cds-modal2-content .imgs-right img{padding-right:40px;padding-top:70px;padding-bottom:70px;object-fit:cover;align-self:center}.cds-modal2-content .secondary-buttons{display:flex;flex-direction:column}.cds-modal2-content .secondary-buttons .button-wrap-1{padding-left:40px;padding-bottom:1em;padding-right:2em}.cds-modal2-content .secondary-buttons .button-wrap-2{margin-bottom:40px;padding-left:40px}.cds-modal2-content .secondary-buttons .button-wrap-2 button{margin-left:3px;border:none}.cds-modal2-content .secondary-buttons button.image-text cds-icon{padding-right:5px}.cds-modal2-content .secondary-buttons.centered .button-wrap-1{padding-left:40px}.cds-modal2-content .secondary-buttons.centered .button-wrap-2{padding-left:0}.cds-modal2-footer.secondary-buttons.centered{padding-bottom:0}@media only screen and (min-width: 990px){.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text){flex-direction:row}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{display:flex;flex:1 1 auto;padding-bottom:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{flex:1 1 auto;display:flex;padding-right:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .button-wrap-1,.cds-modal2-xl .button-wrap-1{padding-left:0!important;padding-right:0;padding-bottom:0!important}.cds-modal2-lg .button-wrap-2,.cds-modal2-xl .button-wrap-2{padding-bottom:0;padding-top:3px;padding-right:0!important;margin-bottom:0!important}.cds-modal2-lg .secondary-buttons,.cds-modal2-xl .secondary-buttons{flex-direction:row}.cds-modal2-lg .secondary-buttons .button-wrap-1,.cds-modal2-xl .secondary-buttons .button-wrap-1{display:flex;flex:1 1 auto}.cds-modal2-lg .secondary-buttons .button-wrap-2,.cds-modal2-xl .secondary-buttons .button-wrap-2{display:flex;flex:1 1 auto;margin-bottom:0}.cds-modal2-lg .secondary-buttons .button-wrap-2 .first,.cds-modal2-xl .secondary-buttons .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .secondary-buttons.centered,.cds-modal2-xl .secondary-buttons.centered{padding-bottom:40px!important}.cds-modal2-lg .secondary-buttons.centered .button-wrap-1,.cds-modal2-xl .secondary-buttons.centered .button-wrap-1{padding-left:0}}.cds-modal2-sm .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1{display:flex;flex-direction:row;padding-right:40px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .secondary-button,.cds-modal2-sm .secondary-buttons .button-wrap-1 .cds-button-tertiary{margin-left:16px}.cds-modal2-sm .secondary-buttons .button-wrap-1 button{margin-top:0}.cds-modal2-sm .secondary-buttons .button-wrap-2{padding-left:0;margin-left:40px}.cds-modal2-sm .secondary-buttons.centered .button-wrap-1 button{flex:1 1 auto}.cds-modal2-sm .secondary-buttons.centered .button-wrap-2{margin-left:0}.cds-modal2-sm .cds-modal2-footer:not(.vertical-buttons) .cds-button-tertiary{margin-top:0;margin-left:16px}.cds-modal2-sm .cds-modal2-footer.vertical-buttons{display:flex;flex-direction:column}.cds-modal2-sm .cds-modal2-footer.vertical-buttons .secondary-button{margin-left:0}.cds-modal2-sm .cds-modal2-footer.vertical-buttons button{align-self:center;width:100%}.cds-modal2-content .display-top{display:none}.cds-modal2-content .display-bottom{display:block}.cds-modal2-dialog-scrollable{display:flex;flex-direction:column}.cds-modal2-dialog-scrollable .cds-modal2-body{overflow-y:auto;max-height:40vh}.cds-modal2-dialog-scrollable .cds-modal2-footer,.cds-modal2-dialog-scrollable .secondary-buttons{padding-top:40px}@media only screen and (min-width: 645px) and (max-width: 990px){.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text){flex-direction:row}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{display:flex;flex:1 1 auto;padding-bottom:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{flex:1 1 auto;display:flex;padding-right:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1,.cds-modal2-xl .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{padding-bottom:0!important;padding-left:0}.cds-modal2-lg .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2,.cds-modal2-xl .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{padding-right:0!important;margin-bottom:0}.cds-modal2-lg .secondary-buttons.triple-button-secondary-text .button-wrap-1,.cds-modal2-xl .secondary-buttons.triple-button-secondary-text .button-wrap-1{padding-left:0}.cds-modal2-lg .secondary-buttons.triple-button-secondary-text .button-wrap-2,.cds-modal2-xl .secondary-buttons.triple-button-secondary-text .button-wrap-2{margin-bottom:0;padding-left:0}.cds-modal2-dialog-scrollable .cds-modal2-body{max-height:25vh}.cds-modal2-dialog-scrollable:has(.cds-modal-border-top) .cds-modal2-body{max-height:10vh}}.cds-modal2-content .imgs-left h5,.cds-modal2-content .imgs-right h5{padding-left:0}@media only screen and (max-width: 645px){.cds-modal2-content .cds-button-tertiary{margin-top:16px;margin-left:0}.cds-modal2-content .cds-button-secondary{margin-right:0;margin-top:16px;margin-left:0}.cds-modal2-dialog-scrollable .cds-modal2-body{height:20vh}.cds-modal2-content .display-top{display:block}.cds-modal2-content .display-bottom{display:none}.cds-modal2-content .imgs-left,.cds-modal2-content .imgs-right{flex-direction:column}.cds-modal2-content .imgs-left img,.cds-modal2-content .imgs-right img{padding-right:0;padding-bottom:0;padding-left:0}.cds-modal2-content .secondary-buttons.centered .button-wrap-2{display:flex;flex-direction:row;margin-bottom:40px}.cds-modal2-sm .cds-button-tertiary{margin-top:16px!important;margin-left:0!important}.cds-modal2-sm .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1{display:flex;flex-direction:column;padding-right:40px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .cds-button-tertiary{margin-top:16px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .secondary-button{margin-top:16px;margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1 button{align-self:center;width:100%}.cds-modal2-sm .secondary-buttons .button-wrap-2{align-self:center;padding-left:0;margin-left:0}.cds-modal2-dialog-scrollable .cds-modal2-sm .cds-button-tertiary{margin-top:16px!important;margin-left:0!important}.cds-modal2-lg .button-wrap-1,.cds-modal2-xl .button-wrap-1{padding-right:0!important;padding-left:0!important}.cds-modal2-lg .button-wrap-2,.cds-modal2-xl .button-wrap-2{display:flex;flex-direction:row;margin-bottom:0}.cds-modal2-content .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-content .secondary-buttons .button-wrap-1{display:flex;flex-direction:column;padding-right:40px}.cds-modal2-content .secondary-buttons .button-wrap-1 button{align-self:center;width:100%}.cds-modal2-content .secondary-buttons .button-wrap-2{align-self:center;padding-left:0}.cds-modal2-content .secondary-buttons .secondary-text{margin-left:0}.cds-modal2-footer{display:flex;flex-direction:column}.cds-modal2-footer .secondary-button{margin-left:0}.cds-modal2-footer button{align-self:center;width:100%}}.cds-modal2-content .no-button{margin-bottom:40px;padding-bottom:0}.cds-modal2-content .close-btn{outline:none;border:none;background-color:transparent}.cds-modal2-content .close-btn:focus{outline:auto}.cds-modal2-body{font-size:15px;padding-left:40px}.cds-modal2-body p,.cds-modal-title{margin-bottom:0}.cds-modal2-footer{display:flex;padding-top:40px;padding-bottom:40px}.cds-modal2-content .secondary-buttons{padding-top:40px}.cds-modal2-content .cds-modal-border-top{border-top:1px solid #cccccc;padding-top:40px;margin-top:20px}.cds-modal2-content .cds-modal-border-bottom{border-bottom:1px solid rgba(0,0,0,.2509803922)}.cds-modal-dialog-centered{display:flex;align-items:center;min-height:100%}.cds-modal-dialog-centered.cds-modal2-dialog-scrollable{justify-content:center}body.cds-modal-open citi-header div.primary-header{z-index:0!important}.cds-button{outline:none}.cds-button-inlineLink{font-family:Interstate_Light,sans-serif;text-decoration:underline;border-radius:6px;border-color:transparent;height:auto;margin:auto 0}.cds-button-inlineLink:focus{box-shadow:0 0 0 1px #002a54 inset}.cds-button-inlineLink:hover,.cds-button-inlineLink:active{text-decoration:underline}.cds-button-inlineLink:disabled{background-color:transparent;border-color:transparent}.cds-button-inlineLink.cds-button-icon,.cds-button-inlineLink.cds-button-icon-round{padding:0!important}.cds-button-inlineLink.cds-button-chevron-right,.cds-button-inlineLink.cds-button-chevron-left{text-decoration:none}.cds-button-tertiary{text-decoration:underline;border-color:transparent}.cds-button-tertiary:hover,.cds-button-tertiary:active{text-decoration:underline}.cds-button-tertiary.cds-button-chevron-right,.cds-button-tertiary.cds-button-chevron-left{text-decoration:none}.cds-button-secondaryInverse cds-icon path{fill:#fff!important}.cds-button-secondaryInverse:focus{box-shadow:0 0 0 1px #002a54,0 0 0 2px #056dae}.cds-modal-title{font-family:Interstate_Regular,sans-serif;font-weight:500;font-size:24px;line-height:32px}.cds-modal-title .copy-right{position:relative;bottom:4;right:8px}.sub-title-first{font-family:Interstate;font-size:16px;font-weight:700;line-height:24px;letter-spacing:0px}.first-p{font-size:16px;line-height:24px}.sub-title-second{font-family:Interstate;font-size:12px;font-weight:700;line-height:18px;letter-spacing:0px;margin-bottom:8px;display:block}.info-content{margin-top:24px;display:block}.mobile-image{width:100%;height:100%;margin-top:5px}.mobile-image img{width:100%;height:100%;padding:0 24px}.qa-link{color:#056dae;text-decoration-line:underline}.close-button-wrap{display:flex;align-items:center;margin-top:16px;height:48px}.close-button-wrap .icon-btn{border:none;margin:0 17px 0 0;padding:16px}.close-button-wrap .icon-btn svg{width:24px;height:24px}.top-margin{margin-top:24px}.padding-left-right{padding-right:24px;padding-left:24px}.cds-modal2-body{text-align:justify}.top-padding{padding-top:24px}\n"] }]
        }], propDecorators: { showCitiMobile: [{
                type: Input
            }], quickHref: [{
                type: Input
            }], qaCitiMobileContent: [{
                type: Input
            }], selectedView: [{
                type: Input
            }], productName: [{
                type: Input
            }], useAppNavigate: [{
                type: Output
            }], notNowCloseModal: [{
                type: Output
            }], emitTaggingForQRcodeCTA: [{
                type: Output
            }], closeCitiModalIcon: [{
                type: Output
            }] } });

const MODULES = [
    CommonModule,
    CdsIconModule,
    CdsModalDialogModule,
    CdsButtonModule
];
class QaCitiMobileModalModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: QaCitiMobileModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: QaCitiMobileModalModule, declarations: [QaCitiMobileModal], imports: [CommonModule,
            CdsIconModule,
            CdsModalDialogModule,
            CdsButtonModule], exports: [QaCitiMobileModal] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: QaCitiMobileModalModule, imports: [MODULES] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: QaCitiMobileModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES],
                    declarations: [QaCitiMobileModal],
                    exports: [QaCitiMobileModal],
                }]
        }] });

/**
 * Represents the SpeedBumpComponent.
 */
class SpeedBumpComponent {
    constructor() {
        this._speedBumpContent = null;
        this.currentData = { head: '', body: '' };
        /**
         * Opens the SpeedBump modal when the Use button is clicked.
         * @twoway false.
         */
        this.showModal = false;
        /**
        * Emits the modalClosed value.
        */
        this.modalClosed = new EventEmitter;
    }
    /**
     * Initializes the component.
     */
    ngOnInit() {
        this.updateCurrentData();
        console.log('SpeedBumpComponent initialized');
    }
    /**
     * Receives speedBump content to display on Modal.
     * @param res The speedBump content.
     */
    set speedBumpContent(res) {
        this._speedBumpContent = res;
        this.updateCurrentData();
    }
    /**
     * Updates the current data based on the current index.
     */
    updateCurrentData() {
        this.currentData.head = this._speedBumpContent?.header || '';
        this.currentData.body = this._speedBumpContent?.body || '';
    }
    /**
     * Handles the "Continue" and "Close" button click event.
     */
    onContinue() {
        this.closeModal();
        this.modalClosed.emit(true);
    }
    /**
     * Closes the modal and resets the current index and data.
     */
    closeModal() {
        this.showModal = false;
        this.modalClosed.emit(false);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SpeedBumpComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: SpeedBumpComponent, isStandalone: true, selector: "speed-bump", inputs: { showModal: "showModal", speedBumpContent: "speedBumpContent" }, outputs: { modalClosed: "modalClosed" }, ngImport: i0, template: `
  <cds-modal-dialog [isOpen]="showModal">
  <div class="cds-modal-dialog-centered">
    <div
      cdsModalInitialFocus
      class="cds-modal2-content cds-modal2-lg"
      role="dialog"
      tabindex="0"
    >
      <div class="close-button-wrap">
        <button
          class="cds-modal2-close close-btn"
          cdsButton="secondary"
          buttonIcon="icon"
          size="small"
          (click)="closeModal()"
          aria-label="Close Modal"
        >
          <cds-icon
            class="close-icon"
            type="2021"
            name="044-close"
            [svgStyle]="{'width.px': 20,'height.px': 20}"
            aria-hidden="true"
          ></cds-icon>
        </button>
      </div>
      <div class="cds-modal2-header">
        <h4 class="cds-modal-title">
          {{this.currentData.head}}
        </h4>
      </div>
      <div class="cds-modal2-body">
        {{this.currentData.body}}
      </div>
      <div class="cds-modal2-footer">
        <button
          class="ml-left"
          cdsButton="primary"
          (click)="onContinue()"
          aria-label = "Continue"
        >
          Continue
        </button>
      </div>
    </div>
  </div>
</cds-modal-dialog>
  `, isInline: true, dependencies: [{ kind: "ngmodule", type: CdsModalDialogModule }, { kind: "component", type: i1.CdsModalDialogComponent, selector: "cds-modal-dialog", inputs: ["isOpen", "isClosedOnBackdropClick", "isClosedOnEscape", "hasFade", "ariaRole", "isRestoreFocusDisabled", "ariaLabel", "ariaLabelledby", "fadeTime"], outputs: ["isOpenChange", "openStart", "openEnd", "closeStart", "closeEnd"] }, { kind: "ngmodule", type: CdsIconModule }, { kind: "component", type: i1.CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }, { kind: "ngmodule", type: CdsButtonModule }, { kind: "directive", type: i1.CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: ["cdsButton", "size", "buttonIcon", "arrowRight", "arrowLeft", "JFPToken"], exportAs: ["cdsButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SpeedBumpComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'speed-bump',
                    template: `
  <cds-modal-dialog [isOpen]="showModal">
  <div class="cds-modal-dialog-centered">
    <div
      cdsModalInitialFocus
      class="cds-modal2-content cds-modal2-lg"
      role="dialog"
      tabindex="0"
    >
      <div class="close-button-wrap">
        <button
          class="cds-modal2-close close-btn"
          cdsButton="secondary"
          buttonIcon="icon"
          size="small"
          (click)="closeModal()"
          aria-label="Close Modal"
        >
          <cds-icon
            class="close-icon"
            type="2021"
            name="044-close"
            [svgStyle]="{'width.px': 20,'height.px': 20}"
            aria-hidden="true"
          ></cds-icon>
        </button>
      </div>
      <div class="cds-modal2-header">
        <h4 class="cds-modal-title">
          {{this.currentData.head}}
        </h4>
      </div>
      <div class="cds-modal2-body">
        {{this.currentData.body}}
      </div>
      <div class="cds-modal2-footer">
        <button
          class="ml-left"
          cdsButton="primary"
          (click)="onContinue()"
          aria-label = "Continue"
        >
          Continue
        </button>
      </div>
    </div>
  </div>
</cds-modal-dialog>
  `,
                    standalone: true,
                    imports: [CdsModalDialogModule, CdsIconModule, CdsButtonModule]
                }]
        }], propDecorators: { showModal: [{
                type: Input
            }], speedBumpContent: [{
                type: Input
            }], modalClosed: [{
                type: Output
            }] } });

/*
 * Public API Surface of citi-utils
 */
// export * from './lib/citi-utils.module';

/**
 * Generated bundle index. Do not edit.
 */

export { AppConfigService, BrowserNavigatorRef, BrowserWindowRef, CitiAppConfig, CitiAppConfigService, CookieService, DataCollectorService, EcmCookieService, FeatureDetectionService, GlobalErrorHandlerService, IS_SSRENDERED_APP, IdleTimeRouterComponent, IdleTimeRouterModule, JSLoaderService, LoggingService, MessagingService, ModalSharedService, NAVIGATOR, NAVIGATOR_PROVIDER, NavigatorRef, PostSessionService, PostSignoffService, QaCitiMobileModal, QaCitiMobileModalModule, RouterRedirectGuard, ScriptLoadService, SessionService, SpeedBumpComponent, StorageService, TAGGING_CONSTANT, TaggingService, UserPersonalizationService, WINDOW, WINDOW_PROVIDER, WindowRef, navigatorFactory, windowFactory };
